import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { CarEntryTypeEnum } from "../../cars-module";
import { PortalTypes } from "../../common";
import {
  FareclassShelfBrandName,
  FlightEntryTypeEnum,
  SliceStopCountFilter,
  TripCategory,
} from "../../flights-module";
import {
  HotelEntryTypeEnum,
  LodgingPromotionType,
  MealPlanKindEnum,
  Reservation,
  RoomProduct,
  RoomProductType,
  RoomProductWithTransformedIndexes,
  TabViewEnum,
} from "../../hotels-module";
import { PackagesEntryTypeEnum } from "../../packages-module";
import {
  EligibilitySegmentEnum,
  ExerciseEligibilityV1,
  ExerciseEligibilityV1Enum,
  FlightDisruptionEnum,
  FlightDisruptionReasonEnum,
  getEligibleSliceFromEligibility,
  HotelCfarCancelScenario,
  HotelCfarCancelScenarioEnum,
  HotelCfarContract,
  ItineraryEnum,
} from "../../trips-module";
import {
  HotelCfarQuote,
  HotelCfarQuoteAdditionalInfo,
  PostBookingTrackingProperties,
} from "./ancillaries";
import { Dealness, PushNotificationFilter, ShopFilter } from "./shop-summary";
import { RoomPaymentSchedule } from "@b2bportal/lodging-api";
import { ActivitiesEntryTypeEnum } from "../../experiences-module";

dayjs.extend(utc);

// COMMON EVENTS

export const ADD_CONTACT_INFO = "add_contact_info";

export interface AddContactInfoProperties {}

export const ADD_TRAVELER = "add_traveler";

export interface AddTravelerProperties {}

export const SELECT_TRAVELERS = "select_travelers";

export interface SelectTravelersProperties {}

export const SELECT_REWARDS = "select_rewards";

export interface SelectRewardsProperties {}

export const ADD_PAYMENT = "add_payment";

export const TRIGGERED_ERROR_BOUNDARY = "triggered_error_boundary";

export interface AddPaymentProperties {
  success: boolean;
  is_vcn: boolean;
}

export const SELECT_PAYMENT = "select_payment";

export interface SelectPaymentProperties {}

export const APPLIED_TRAVEL_CREDITS = "applied_travel_credits";

export interface AppliedTravelCreditsProperties {
  funnel?: "packages" | "flights" | "hotels" | "cars";
}

export const APPLIED_TRAVEL_OFFER = "applied_travel_offer";

export interface AppliedTravelOfferProperties {
  funnel?: "packages" | "flights" | "hotels" | "cars";
}

export const RESTRICTED_TRAVEL_CREDIT_TOGGLED =
  "restricted_travel_credit_toggled";

export interface RestrictedTravelCreditToggledProperties {
  selected: boolean;
  applied_amount?: number;
  credit_id: string;
  credit_product_name: string;
  credit_restrictions: string;
  lodging_id?: string;
}

export const EDITED_RESTRICTED_CREDIT_AMOUNT =
  "edited_restricted_credit_amount";

export interface EditedRestrictedCreditAmountProperties {
  credit_id: string;
  credit_product_name: string;
  applied_amount: number;
  previous_amount: number;
  credit_restrictions: string;
  lodging_id?: string;
}

export const RESTRICTED_TRAVEL_CREDIT_DISPLAYED =
  "restricted_travel_credit_displayed";

export interface RestrictedTravelCreditDisplayedProperties {
  credit_id: string;
  credit_product_name: string;
  credit_restrictions: string;
  lodging_id?: string;
}

// AIR EVENTS

export const VIEWED_AIR_SEARCH = "viewed_air_search";
export interface ViewedAirSearchProperties {
  entry_type?: FlightEntryTypeEnum;
  entry_types_all?: FlightEntryTypeEnum[];
}

export const AIR_ENTRY = "air_entry";
export const CHAT_ENTERED = "chat_entered";

export interface AirEntryProperties {
  delegated_to: string;
  first_launch: boolean;
  origin: string;
  destination: string;
  origin_country_code: string;
  destination_country_code: string;
  departure_date: string;
  return_date?: string;
  pax_total: number;
  number_of_active_watches: number;
  trip_type: "round_trip" | "one_way" | "multi_city";
  has_credits: boolean;
  credit_balance: number;
  vx_statement_credit_balance: number;
  rewards_accounts?: string;
  customer_account_role?: string;
  entry_type?: FlightEntryTypeEnum;
  fareclass_filter?: string[];
  non_stop?: boolean;
  preferences_applied?: boolean;
  cross_sell_displayed?: boolean;
  card_on_file?: boolean;
  entry_types_all?: FlightEntryTypeEnum[];
}

export const AIR_MULTICITY_ENTRY = "air_multicity_entry";

export interface AirMultiCityEntryProperties {
  first_launch: boolean;
  flight1_origin: string;
  flight1_origin_country_code: string;
  flight1_destination: string;
  flight1_destination_country_code: string;
  flight1_departure_date: string;
  flight2_origin: string;
  flight2_origin_country_code: string;
  flight2_destination: string;
  flight2_destination_country_code: string;
  flight2_departure_date: string;
  flight3_origin?: string;
  flight3_origin_country_code?: string;
  flight3_destination?: string;
  flight3_destination_country_code?: string;
  flight3_departure_date?: string;
  flight4_origin?: string;
  flight4_origin_country_code?: string;
  flight4_destination?: string;
  flight4_destination_country_code?: string;
  flight4_departure_date?: string;
  flight5_origin?: string;
  flight5_origin_country_code?: string;
  flight5_destination?: string;
  flight5_destination_country_code?: string;
  flight5_departure_date?: string;
  pax_total: number;
  number_of_active_watches: number;
  trip_type: "multi_city";
  has_credits: boolean;
  credit_balance: number;
  vx_statement_credit_balance: number;
  rewards_accounts?: string;
  customer_account_role?: string;
  entry_type?: FlightEntryTypeEnum;
  entry_types_all?: FlightEntryTypeEnum[];
}

export const VIEWED_FORECAST = "viewed_forecast";
export const VIEWED_FLIGHT_LIST = "viewed_flight_list";
export const VIEWED_MULTICITY_FLIGHT_LIST = "viewed_multicity_flight_list";

export interface PredictionProperties {
  recommendation: "buy" | "wait" | "null";
  dealness: Dealness | string;
}

export interface SharedFlightListProperties {
  flights_shown?: number;
  shelf_fares?: {
    [key: string]: number; // number of fares by each fare shelf. This is flattened as separate key-value pairs below for Amplitude
  };
  // There are 5 standard fare shelves as defined here https://hopper-jira.atlassian.net/wiki/spaces/AS/pages/5462983101/Fare+Shelf+Assignment#Mapping-Schema
  shelf0_fares?: number; // Basic fares
  shelf1_fares?: number; // Standard fares
  shelf2_fares?: number; // Enhanced fares
  shelf3_fares?: number; // Premium fares
  shelf4_fares?: number; // Luxury fares
}

export interface AccountProperties {
  account_type_selected: string;
  account_use_type?: string;
  customer_account_role?: string;
  account_allow_rewards_redemption?: boolean;
}

export interface ViewedFlightListProperties
  extends AirEntryProperties,
    SharedFlightListProperties,
    PredictionProperties,
    AccountProperties {
  slice_direction?: "outbound" | "return";
  fares_shown_outbound?: number;
  fares_shown_return?: number;
  includes_hopper_vi?: boolean;
  hopper_vi_savings_usd?: number;
  hopper_vi_duration_savings_minutes?: number;
  hopper_vi_flights_shown?: number;
  multi_ticket_type?: string;
  includes_hackerfare?: boolean;
  hackerfare_savings_usd?: number;
  hackerfare_flights_shown?: number;
  no_availability?: boolean;
  recommended_flight_displayed?: boolean;
  funnel?: string;
}

export interface ViewedMulticityFlightListProperties
  extends AirEntryProperties,
    SharedFlightListProperties,
    AccountProperties {
  slice_index?: number;
  fares_shown?: number;
  page?: "flight_list" | "review_itinerary" | "pf_checkout";
}

export const VIEWED_DURATION_SLIDER = "viewed_duration_slider";

export const CONTINUE_DURATION_SLIDER = "continue_duration_slider";

export const APPLIED_FILTER = "applied_filter";

export type AppliedPriceWatchFilterPropertyType =
  | "exclude_LCC"
  | "include_LCC"
  | "stops_watch"
  | "any_stops_watch";

export type AppliedFilterPropertyType =
  | "price"
  | "stops"
  | "airline"
  | "time"
  | "airport"
  | "flight number"
  | "exclude_LCC"
  | "baggage_transfer"
  | "duration"
  | "fareclass"
  | "policy_status"
  | AppliedPriceWatchFilterPropertyType;

export interface AppliedFilterProperties {
  filter_type: AppliedFilterPropertyType;
  location: "search" | "list";
  funnel?: "packages";
}

export const VIEWED_SLICE = "viewed_slice";
export const VIEWED_MULTICITY_SLICE = "viewed_multicity_slice";

export interface ViewedSliceProperties extends ViewedFlightListProperties {
  fare_class: string;
}

export const SELECTED_FLIGHT = "selected_flight";
export const SELECTED_MULTICITY_FLIGHT = "selected_multicity_flight";

export interface SelectedSliceProperties extends ViewedSliceProperties {
  flight_list_ranking?: number;
  multi_ticket_type?: string;
}

export const VIEWED_TRIP_SUMMARY = "viewed_trip_summary";
export const VIEWED_MULTICITY_TRIP_SUMMARY = "viewed_multicity_trip_summary";

export interface ViewedTripSummaryProperties extends SelectedSliceProperties {
  carrier: string;
  is_multi_ticket: boolean;
  multi_ticket_type?: string;
  entry_type?: FlightEntryTypeEnum;
  entry_types_all?: FlightEntryTypeEnum[];
}

export const ADD_FREQUENT_FLYER = "add_frequent_flyer";

export interface AddFrequentFlyerProperties {
  frequent_flyer_program: string;
}

export const ADD_PASSPORT = "add_passport";

export interface AddPassportProperties {
  success: boolean;
}

export const VIEW_ITINERARY_RESTRICTIONS = "view_itinerary_restrictions";
export const REVIEW_FLIGHT_DETAILS = "review_flight_details";
export const REVIEW_MULTICITY_FLIGHT_DETAILS =
  "review_multicity_flight_details";

export interface ReviewFlightDetailsProperties
  extends ViewedTripSummaryProperties,
    Cap1DpOfferFactsProperties {
  stops: number;
  provider: string;
  advance: number;
  carrier: string;
  multi_ticket_type?: string;
  business_loyalty_programs?: string;
  slice_count?: number;
  is_same_day_round_trip?: boolean;
}

export const AIR_SAVE_AND_CONTINUE_CLICKED = "air_save_and_continue_clicked";
export const AIR_PRICE_QUOTE_TO_BOOK_REQUEST_RUNTIME =
  "air_price_quote_to_book_request_runtime";

export const PRICE_QUOTE_AIR = "price_quote_air";
export const PRICE_QUOTE_MULTICITY_AIR = "price_quote_multicity_air";

export interface PriceQuoteAirProperties extends ReviewFlightDetailsProperties {
  success: boolean;
  booking_session_id: string;
  pax_total: number;
  flight_list_ranking?: number;
  x_price_difference: number;
}

export const COMPLETE_BUY_AIR = "complete_buy_air";
export const COMPLETE_BUY_MULTICITY_AIR = "complete_buy_multicity_air";

export interface CompleteBuyAirProperties
  extends ReviewFlightDetailsProperties {
  success: boolean;
  total_rewards_amount_usd: number;
  total_card_amount_usd: number;
  card_product_used: string;
  rewards_product_used: string;
  rewards_currency: string;
  agent_book_fee_amount_usd: number;
  pax_total: number;
  agent_locator: string;
  booking_session_id: string;
  seats_selection_available: boolean;
  seats_selected_seats: boolean;
  seats_selected_count: number;
  seats_selected_paid: number;
  seats_selected_free: number;
  seats_selected_price_usd: number;
  lowest_price_seat: number;
  flight_list_ranking?: number;
  credit_redeemed: boolean;
  credit_amt_redeemed: number;
  is_vcn: boolean;
  preferences_recommended?: boolean;
  purpose_of_travel?: string;
  booked_self_name_match: boolean;
  x_price_difference: number;
  frequent_flyer_added_count?: number;
  is_corporate?: boolean;
}

export interface PriceAlertPreferenceProperties {
  /*
                  true = user can receive push notifications & we show them the opt in
                  false = user can’t receive push notifications & we don’t show them the opt in
                */
  push_eligibile?: boolean;
  /*
                  push = user opts into push
                  email = user doesn’t opt into push or is not eligible for push
                */
  channel?: "push" | "email";
}

export const getPriceAlertPreferenceProperties = (
  pushNotificationFilter?: PushNotificationFilter,
): PriceAlertPreferenceProperties => {
  return {
    push_eligibile: !!pushNotificationFilter,
    channel:
      pushNotificationFilter === PushNotificationFilter.AppNotifications
        ? "push"
        : "email",
  };
};

export const VIEWED_WATCH = "viewed_watch";

export interface ViewedWatchProperties extends PriceAlertPreferenceProperties {}

export const ADDED_WATCH = "added_watch";

export const SELECTED_WATCH_PUSH = "selected_watch_push";

export interface SelectedWatchPushProperties {
  action: "opt-in" | "opt-out";
}

export enum WatchFilterType {
  NonStopExcludeLCC = "non_stop_exclude_LCC",
  NonStopIncludeLCC = "non_stop_include_LCC",
  AnyStopsExcludeLCC = "any_stops_exclude_LCC",
  AnyStopsIncludeLCC = "any_stops_include_LCC",
}

export interface AddedWatchProperties extends PriceAlertPreferenceProperties {
  recommendation?: "wait" | "buy";
  filter_type:
    | WatchFilterType.NonStopExcludeLCC
    | WatchFilterType.NonStopIncludeLCC
    | WatchFilterType.AnyStopsExcludeLCC
    | WatchFilterType.AnyStopsIncludeLCC;
}

export const getWatchFilterTypeFromShopFilter = (
  shopFilter: ShopFilter | undefined,
) => {
  switch (shopFilter) {
    case ShopFilter.NonStopNoLCC:
      return WatchFilterType.NonStopExcludeLCC;
    case ShopFilter.NonStop:
      return WatchFilterType.NonStopIncludeLCC;
    case ShopFilter.NoLCC:
    case ShopFilter.ShortLayoverNoLCC:
      return WatchFilterType.AnyStopsExcludeLCC;
    case ShopFilter.ShortLayover:
    case ShopFilter.NoFilter:
    case undefined:
      return WatchFilterType.AnyStopsIncludeLCC;
  }
};

export const getWatchFilterTypeFromFlightShopParams = (
  stopOption: SliceStopCountFilter,
  noLcc: boolean,
): WatchFilterType => {
  if (noLcc) {
    switch (stopOption) {
      case SliceStopCountFilter.NONE:
        return WatchFilterType.NonStopExcludeLCC;
      default:
        return WatchFilterType.AnyStopsExcludeLCC;
    }
  } else {
    switch (stopOption) {
      case SliceStopCountFilter.NONE:
        return WatchFilterType.NonStopIncludeLCC;
      default:
        return WatchFilterType.AnyStopsIncludeLCC;
    }
  }
};

export const APPLIED_FREQUENT_FLYER = "applied_frequent_flyer";
export const SKIPPED_SEAT_SELECTION = "skipped_seats_selection";
export const REQUESTED_SEATMAP = "requested_seatmap";
export const VIEWED_SEATMAP = "viewed_seatmap";
export const CHOSE_FROM_SEATMAP = "chose_from_seatmap";

export const CHANGED_TRIP_TYPE = "changed_trip_type";
export const CHANGED_PAX_COUNT = "changed_pax_count";

export interface ITrackingProperties<T = any> {
  properties: T;
  encryptedProperties: Array<string>;
}

// HOTEL EVENTS
export const HOTEL_ENTRY = "hotel_entry";

export interface HotelEntryProperties {
  delegated_to: string;
  first_launch: boolean;
  market: string;
  country: string;
  number_of_properties: number;
  number_of_guests: number;
  hotel_advance: string;
  advance?: number;
  rooms_searched: number;
  pets_searched?: number;
  has_credits: boolean;
  credit_balance: number;
  vx_statement_credit_balance: number;
  rewards_accounts?: string;
  customer_account_role?: string;
  entry_type?: HotelEntryTypeEnum;
  los?: number;
  preferences_applied?: boolean;
  card_on_file?: boolean;
  adults_count: number;
  children_count: number;
  number_of_filters_applied?: number;
  sort_order?: string;
  destination_name?: string;
}

export const VIEWED_HOTEL_LIST = "viewed_hotel_list";

export interface ViewedHotelListProperties
  extends HotelEntryProperties,
    TravelWalletOfferTrackingProperties,
    SharedTravelOfferTrackingProperties {
  account_type_selected: string;
  account_use_type?: string;
  customer_account_role?: string;
  account_allow_rewards_redemption?: boolean;
  unavailable_hotels: number;
  available_hotels: number;
  pc_shown: number;
  has_pc_properties?: boolean;
  has_lodging_promotions?: number;
  lc_shown: number;
  has_lc_properties?: boolean;
  city_name?: string;
  state?: string;
  no_availability?: boolean;
  google_place_id?: string;
  available_vr?: number;
  vr_pc_shown?: number;
  vr_lc_shown?: number;
}

export const VIEWED_LODGING_IN_LIST = "viewed_lodging_in_list";

export const VIEWED_CURATED_HOTEL = "viewed_curated_hotel";

export const CLICKED_CURATED_HOTEL = "clicked_curated_hotel";

export const VIEWED_UNAVAILABLE_HOTEL_LIST = "viewed_unavailable_hotel_list";

export interface ViewedUnavailableHotelListProperties
  extends ViewedHotelListProperties {}

export const SELECTED_UNAVAILABLE_HOTEL_FROM_LIST =
  "selected_unavailable_hotel_from_list";

export interface SelectedUnavailableHotelFromList
  extends ViewedHotelListProperties {}

export const APPLIED_HOTEL_FILTER = "applied_hotel_filter";

export interface AppliedHotelFilterProperties {
  filter_type:
    | "stars"
    | "price"
    | "hotel_name"
    | "amenities"
    | "vr_amenities"
    | "vr_rooms_and_beds"
    | "premier_collection"
    | "free_cancel"
    | "on_sale"
    | "policy_status"
    | "loyalty_programs"
    | "premium_stays"
    | "lifestyle_stays"
    | "stay_types"
    | "meal_plan_types"
    | "package_discount";
  amenities?: string[];
  tab_view?: string;
  property_name?: string;
  location?: "list" | "all_filters_modal" | "map";
}

export interface AppliedPremiumStaysToggleProperties
  extends AppliedHotelFilterProperties {
  filter_type: "premium_stays";
  searched_market: string | null;
}

export interface AppliedLifestyleStaysToggleProperties
  extends AppliedHotelFilterProperties {
  filter_type: "lifestyle_stays";
  searched_market: string | null;
}

export const APPLIED_PREMIUM_SEARCH_FILTER = "applied_premium_search_filter";

export enum PremiumStaysFunnel {
  LuxuryHotel = "luxury_hotel",
  LuxuryVR = "luxury_vr",
}

export enum PremiumStaysFilterType {
  ResetFilters = "reset_filters",
  AllFilters = "all_filters",
  SortBy = "sort_by",
  Amenities = "amenities",
  Rating = "rating",
  FreeCancellation = "free_cancellation",
  Bedrooms = "bedrooms",
  Beds = "beds",
  Bathrooms = "bathrooms",
  MaxPrice = "max_price",
  HotelName = "hotel_name",
  MealPlan = "meal_plan",
}

export interface AppliedPremiumSearchFilterProperties {
  funnel: PremiumStaysFunnel;
  filter_type: PremiumStaysFilterType;
  amenities: string[];
  rating: number[];
  free_cancellation: boolean;
  bedrooms?: number;
  beds?: number;
  bathrooms?: number;
  max_price: number;
  sort_by: string;
  hotel_name?: string;
  meal_plan?: string[];
}

export const SELECTED_HOTEL_FROM_LIST = "selected_hotel_from_list";

export interface SelectedHotelFromListProperties
  extends ViewedHotelListProperties {
  is_preferred_cot: boolean;
  is_pc: boolean;
  is_lc: boolean;
  nth_night_promo?: string;
  meal_plan?: MealPlanKindEnum;
}

// When a hotel/VR modal is clicked and the cover page is opened
export const SELECTED_HOTEL_FROM_MAP = "selected_hotel_from_map";

export interface SelectedHotelFromMapProperties
  extends ViewedHotelListProperties {
  is_preferred_cot: boolean;
  is_pc: boolean;
  is_lc: boolean;
}

// When the "Search this area" button is clicked on the map
export const SEARCH_THIS_AREA = "search_this_area";

// When a marker (hotel, stay, cluster) is clicked on the map (opens the map modal or expands the cluster)
export const CLICKED_LODGING_MAP = "clicked_lodging_map";

export enum ClickedLodgingMapEnum {
  HOTEL = "hotel",
  VACATION_RENTAL = "vacation_rental",
  CLUSTER = "cluster",
}

export enum MapFunnelEnum {
  STAYS = "stays",
  PACKAGES = "packages",
}

export interface ClickedLodgingMapProperties extends ViewedHotelListProperties {
  click_type: ClickedLodgingMapEnum;
  /**
   * True if the user has not clicked "Search this area" or used the "View stays near..." search.
   * If a new search is performed, this resets to true.
   */
  initial_search: boolean;
  map_funnel: MapFunnelEnum;
}

export const VIEWED_HOTEL_DETAILS = "viewed_hotel_details";

export interface ViewedHotelDetailsProperties
  extends ViewedHotelListProperties {
  account_type_selected: string;
  rooms_shown: number;
  is_preferred_cot: boolean;
  is_pc: boolean;
  is_lc: boolean;
  entry_type?: HotelEntryTypeEnum;
  promotion_applicable: LodgingPromotionType;
  promotion_discount: number;
  promotion_discount_currency: string;
  promotion_discount_percentage: number;
  cfar_rr_offer: number;
  rooms_with_images_count: number;
  rooms_without_images_count: number;
  hotel_details_entry_source?: string;
  nth_night_promo?: string;
  meal_plan?: MealPlanKindEnum;
}

export const SEARCH_DISTANCE_FROM_SHOP = "search_distance_from_shop";
export const VIEWED_HOTEL_SHOP_MAP = "viewed_hotel_shop_map";

export interface SearchDistanceFromShopProperties
  extends ViewedHotelDetailsProperties {
  point_of_interest: string;
}

export const VIEW_HOTELS_NEAR = "view_hotels_near";

export interface ViewHotelsNearAvailabilityProperties
  extends ViewedHotelListProperties {
  poi_availability: string;
}

export const ADD_DRIVERS_LICENSE = "add_drivers_license";

export const SELECT_HOTEL_ROOM = "select_hotel_room";

export interface SelectHotelRoomProperties
  extends ViewedHotelDetailsProperties {
  room_type: string;
  account_type_selected: string;
}

export const REVIEW_DETAILS_HOTEL_CHECKOUT = "review_details_hotel_checkout";

export interface ReviewDetailsHotelCheckoutProperties
  extends ViewedHotelListProperties {
  lodging_name: string;
  market: string;
  chain: string;
  brand: string;
  length_of_stay: number;
  sell_price_excluding_taxes_and_fees_usd: number;
  owed_now_excluding_taxes_and_fees: number;
  owed_now_taxes_and_fees: number;
  owed_at_checkin_taxes_and_fees: number;
  pay_later_excluding_taxes_and_fees: number;
  pay_later_taxes_and_fees: number;
  pay_later_fees: number;
  total_discount: number;
  refundability: "non_refundable" | "free_cancellation";
  supply_revenue_usd: number;
  check_in_date: string;
  check_out_date: string;
  supplier: string;
  hotel_advance: string;
  provider_name?: string;
  provider_type: "direct" | "retail" | "wholesale";
  rate_provider?: string;
  adr: string;
  is_preferred_cot: boolean;
  in_policy?: boolean;
  hotel_loyalty_eligible?: boolean;
  is_corporate?: boolean;
  payment_schedule?: RoomPaymentSchedule;
  // Promotional-rate analytics.
  promotion_type?: "MemberOnlyDeal" | "NthNight";
  promotion_amount?: number;
}

export const HOTEL_SAVE_AND_CONTINUE_CLICKED =
  "hotel_save_and_continue_clicked";

export const PRICE_QUOTE_HOTEL = "price_quote_hotel";

export interface PriceQuoteHotelProperties
  extends ReviewDetailsHotelCheckoutProperties {
  success: boolean;
  booking_session_id: string;
  lodging_row_index?: number;
  x_price_difference: number;
}

export const PRICE_QUOTE_VR = "price_quote_vr";

export interface PriceQuoteVrProperties
  extends ReviewDetailsHotelCheckoutProperties {
  success: boolean;
  booking_session_id: string;
  lodging_row_index?: number;
  x_price_difference: number;
  // VR-specific properties
  number_of_bedrooms?: number;
  number_of_bathrooms?: number;
  number_of_beds?: number;
  max_occupancy?: number;
  homes_star_rating?: number;
  homes_user_rating?: number;
  homes_number_of_reviews?: number;
  // Corporate-specific properties
  in_policy?: boolean;
  policy_reason?: string;
}

export const COMPLETE_BUY_HOTEL = "complete_buy_hotel";

export interface HotelCrossSellTrackingProperties {
  feed_placement?: string;
  origin_product_type?: string;
  xsell_click_target?: string;
  tile_index?: number;
}

export interface CompleteBuyHotelProperties
  extends ReviewDetailsHotelCheckoutProperties,
    HotelCrossSellTrackingProperties {
  success: boolean;
  total_rewards_amount_usd: number;
  total_card_amount_usd: number;
  card_product_used: string;
  rewards_product_used: string;
  rewards_currency: string; // ie. “Miles”, “Cash”, “Points”
  agent_booking_fee_amount_usd: number;
  booking_session_id: string;
  reservation_id: string;
  lodging_row_index?: number;
  is_recommended?: boolean;
  rooms_booked: number;
  is_pc: boolean;
  is_lc: boolean;
  credit_redeemed: boolean;
  credit_amt_redeemed: number;
  refundable_advance?: string;
  cancellation_policy?: string;
  entry_type?: HotelEntryTypeEnum;
  is_vcn: boolean;
  is_recent: boolean;
  in_policy?: boolean;
  hotel_loyalty_eligible?: boolean;
  is_corporate?: boolean;
  preferences_recommended?: boolean;
  searched_market?: string;
  total_guests: number;
  city_name?: string;
  state?: string;
  purpose_of_travel?: string;
  nth_night_promo?: string;
  meal_plan?: MealPlanKindEnum;
  booked_self_name_match: boolean;
  x_price_difference: number;
  card_matched_rewards_account?: boolean;
  loyalty_program_code?: string;
  user_has_loyalty_for_displayed_program?: boolean;
}

export const SELECTED_RECOMMENDED_HOTEL = "selected_recommended_hotel";

export interface SelectedRecommendedHotelProperties
  extends SelectedHotelFromListProperties {}

export const HOTEL_LIST_MAP_CAROUSEL = "hotel_list_map_carousel";

export const ADD_HOTEL_LOYALTY = "add_hotel_loyalty";

export interface AddHotelLoyaltyProperties {
  loyalty_program_code?: string;
}

// CARS EVENTS
export const VIEWED_CARS_SEARCH = "viewed_cars_search";

export interface ViewedCarsSearchProperties {
  entry_type?: CarEntryTypeEnum;
  entry_types_all?: CarEntryTypeEnum[];
}

export const CARS_ENTRY = "cars_entry";

export interface CarsEntryProperties {
  delegated_to: string;
  first_launch: boolean;
  market: string;
  country: string;
  airport: string;
  pick_up_date: string;
  pick_up_hour: number;
  drop_off_date: string;
  drop_off_hour: number;
  search_age: number;
  same_pickUp_dropOff: boolean;
  cars_advance: number;
  has_credits: boolean;
  credit_balance: number;
  vx_statement_credit_balance: number;
  rewards_accounts?: string;
  customer_account_role?: string;
  entry_type?: string;
  card_on_file?: boolean;
  entry_types_all?: string[];
}

export const VIEWED_CARS_LIST = "viewed_cars_list";

export interface ViewedCarsListProperties
  extends CarsEntryProperties,
    Partial<TravelWalletOfferTrackingProperties>,
    SharedTravelOfferTrackingProperties {
  account_type_selected: string;
  number_of_results: number;
  results_from_supplier_ABG?: number;
  results_from_supplier_AM?: number;
  results_from_supplier_HE?: number;
  results_from_supplier_PL?: number;
  results_from_supplier_SX?: number;
  cheapest_price: number;
  cheapest_provider: string;
  pick_up_place_country: string;
  pick_up_place_city: string;
  pick_up_place_id: string;
  drop_off_place_country: string;
  drop_off_place_city: string;
  drop_off_place_id: string;
  account_use_type?: string;
  customer_account_role?: string;
  account_allow_rewards_redemption?: boolean;
  card_on_file?: boolean;
}

export const APPLIED_CARS_FILTER = "applied_cars_filter";

export interface AppliedCarsFilterProperties extends CarsFilterEventProperties {
  filter_type:
    | "reset"
    | "car_type"
    | "price"
    | "rental_company"
    | "specifications"
    | "cancellation_policy"
    | "passengers"
    | "bags"
    | "pickup"
    | "policy_status";
}

export const SELECTED_CAR_FROM_LIST = "selected_car_from_list";

export interface SelectedCarFromListProperties
  extends ViewedCarsListProperties {
  vehicle_type: string;
  total_price: number;
  free_cancellation: boolean;
  unlimited_mileage: boolean;
  automatic: boolean;
  supplier: string;
  vendor: string;
  non_refundable: boolean;
  conditional_cancellation: boolean;
  conditional_cancellation_after_booking: boolean;
  in_policy?: boolean;
}

export const VIEWED_CAR_DETAILS = "viewed_car_details";

export interface ViewedCarDetailsProperties
  extends SelectedCarFromListProperties {
  account_type_selected: string;
  fuel_policy: string;
  pax_count?: number;
  bags_count?: number;
  pickup_location?: string;
  car_type?: string;
  same_dropoff?: boolean;
}

export const REVIEWED_CAR_DETAILS_CHECKOUT = "reviewed_car_details_checkout";

export interface ReviewedCarDetailsCheckoutProperties
  extends ViewedCarDetailsProperties {
  driver_age: number;
  pay_now: number;
  pay_later: number;
  estimated_commission: number;
}

export const CAR_SAVE_AND_CONTINUE_CLICKED = "car_save_and_continue_clicked";

export const PRICE_QUOTE_CAR = "price_quote_car";

export interface PriceQuoteCarProperties
  extends ReviewedCarDetailsCheckoutProperties {
  success: boolean;
  booking_session_id: string;
  x_price_difference: number;
}

export const COMPLETE_BUY_CARS = "complete_buy_car";

export interface CompleteBuyCarProperties
  extends ReviewedCarDetailsCheckoutProperties {
  success: boolean;
  total_rewards_amount_usd: number;
  total_card_amount_usd: number;
  card_product_used: string;
  rewards_product_used: string;
  rewards_currency: string;
  agent_booking_fee_amount_usd: number;
  booking_session_id: string;
  ground_booking_id: string;
  credit_redeemed: boolean;
  credit_amt_redeemed: number;
  is_vcn: boolean;
  entry_type?: CarEntryTypeEnum;
  in_policy?: boolean;
  purpose_of_travel?: string;
  booked_self_name_match: boolean;
  x_price_difference: number;
  entry_types_all?: CarEntryTypeEnum[];
  loyalty_program_added?: boolean;
  car_loyalty_eligible?: boolean;
}

export const ADD_HERTZ_LOYALTY_REWARDS = "add_hertz_loyalty_rewards";

export interface AddHertzLoyaltyRewardsProperties {}

// MY TRIPS EVENTS
export const VIEWED_MY_TRIPS = "viewed_my_trips";

export const VIEWED_CONTACT_MODAL = "viewed_contact_modal";

export interface MyTripsProperties {
  agent_locator: string;
  agent_locator_provider: string;
  days_until_departure: string;
  booking_date: string;
  has_skch?: string;
  skch_severity?: string;
  cfar_active?: number;
}

export interface ViewedContactModalProperties extends MyTripsProperties {
  contact_reason:
    | "cancel_flight"
    | "change_flight"
    | "change_hotel"
    | "cancel_hotel"
    | "change_car"
    | "cancel_car"
    | "general_contact";
  contact_provider_air?: string;
  contact_provider_hotel?: string;
  contact_provider_ground?: string;
}

export const WATCH_ENTRY = "watch_entry";
export const SELECT_WATCHED_FLIGHT = "select_watched_flight";
export const STOPPED_WATCH = "stopped_watch";

export interface FlightWatchBaseProperties {
  advance: number;
  destination: string;
  origin: string;
  departure_date: string;
  return_date?: string;
  trip_type: TripCategory;
  filter_type: WatchFilterType;
}

export interface WatchEntryProperties extends FlightWatchBaseProperties {}

export interface SelectWatchedFlightProperties
  extends FlightWatchBaseProperties {
  number_of_active_watches: number;
}

export interface StoppedWatchProperties {
  success: boolean;
}

export const VIEWED_MAJOR_SCHEDULE_CHANGE = "viewed_major_schedule_change";

export interface ViewedMajorScheduleChangeProperties
  extends MyTripsProperties {}

export const USER_ACTION_SCHEDULE_CHANGE_SUCCESS =
  "user_action_schedule_change_success";

export interface UserActionScheduleChangeSuccessProperties {
  action: string;
  user_action: string;
  severity: string;
  system_locator_provider: string;
  agent_locator_provider: string;
  agent_locator: string;
  system_locator: string;
  days_until_departure: string;
  connection_time_valid: string;
  is_continuous: string;
  has_reprotection: string;
  provider: string;
  pcc: string;
  booking_date: string;
  itinerary_id: string;
  validating_carrier: string;
}

export const SCHEDULE_CHANGE_DECISION = "schedule_change_decision";

export interface ScheduleChangeDecisionProperties extends MyTripsProperties {
  decision_outcome: "accept" | "deny";
}

export const AUTO_ACCEPT = "auto_accept";

// ONBOARDING EVENTS

export const VIEWED_ONBOARDING = "viewed_onboarding";
export const COMPLETED_ONBOARDING = "completed_onboarding";
export const SKIPPED_ONBOARDING = "skipped_onboarding";

// FTUX

export const PRICE_DROP_CREDIT = "price_drop_credit";

export interface PriceDropCreditProperties {
  price_bucket?: PriceBucketValue | null;
  refund_type?: "cash" | "credit" | null;
}

type PriceBucketValue = "great_price" | "good_price" | "fair_price" | "wait";

export const getPriceBucketValueFromDealness = (
  dealness: Dealness,
): PriceBucketValue | null => {
  switch (dealness) {
    case Dealness.Wait:
      return "wait";
    case Dealness.Fair:
      return "fair_price";
    case Dealness.Good:
      return "good_price";
    case Dealness.Great:
      return "great_price";
    default:
      return null;
  }
};

export const PRICE_DROP_VIEWED = "price_drop_viewed";

export interface PriceDropViewedProperties extends PriceDropCreditProperties {
  page:
    | "watched_trips"
    | "forecast"
    | "flight_list"
    | "trip_summary"
    | "flight_book"
    | "payment_summary"
    | "trip_review"
    | "flight_confirmation";
  price_drop_offer_duration: number;
  price_drop_offer_max_cap: number;
  price_drop_offer_min_cap: number;
}

export const VIEWED_PRICE_DROP_DETAILS = "viewed_price_drop_details";

export interface ViewedPriceDropDetailsProperties
  extends PriceDropCreditProperties {
  page:
    | "watched_trips"
    | "forecast"
    | "flight_list"
    | "trip_summary"
    | "flight_book"
    | "mobile"
    | "trip_review"
    | "flight-confirmation";
}

export const PRICE_DROP_ADDED = "price_drop_added";

export interface PriceDropAddedProperties extends PriceDropCreditProperties {
  price_drop_offer_duration: number;
  price_drop_offer_max_cap: number;
  price_drop_offer_min_cap: number;
}

export const PRICE_DROP_REFUND_RECEIVED = "price_drop_refund_received";

export interface PriceDropRefundReceivedProperties
  extends PriceDropCreditProperties {
  success: boolean;
  drop_amount: number;
}

export const CREDITS_ISSUED = "credits_issued";

export interface CreditsIssuesProperties extends PriceDropCreditProperties {
  credit_issued_reason: string;
}

// GENERAL EVENTS & FAILURES

export interface RuntimeProperties {
  runtime: number;
  provider?: string;
  carrier?: string;
}

export type LandingScreen =
  | "cars_list"
  | "cars_search"
  | "exchange"
  | "flight_search"
  | "flight_shop"
  | "ftux"
  | "hotel_list"
  | "hotel_search"
  | "my_trips"
  | "travel_wallet";

export const LAUNCHED_APPLICATION = "launched_application";

export interface LaunchedApplicationProperties {
  landing_screen: LandingScreen;
  url: string;
  rewards_accounts: string;
  has_credits: boolean;
  credit_balance: number;
  vx_statement_credit_balance: number;
  user_source?: string;
  utm_medium?: string;
  spend_and_get_offer_progress?: number;
  spend_and_get_offer_max_credits?: number;
  spend_and_get_offer_cancelled?: boolean;
}

export const LOGGED_IN = "logged_in";

export interface LoggedInProperties {
  runtime: number;
}

export const LAUNCHED_MOBILE_LOCATION_SCREEN =
  "launched_mobile_location_screen";

export interface LaunchedMobileLocationScreenProperties {
  funnel:
    | "flights"
    | "cars"
    | "hotels"
    | "premier_collection"
    | "experiences"
    | "other";
  url: string;
  entry_type?: string;
  entry_types_all?: string[];
}

export const AUTOCOMPLETE_LOCATION_SELECTED = "autocomplete_location_selected";

export interface AutocompleteLocationSelectedProperties {
  funnel:
    | "flights"
    | "cars"
    | "hotels"
    | "premier_collection"
    | "experiences"
    | "other";
  url: string;
  autocomplete_value: string;
}

export const LAUNCHED_MOBILE_CALENDAR = "launched_mobile_calendar";

export interface LaunchedMobileCalendarProperties {
  funnel:
    | "flights"
    | "cars"
    | "hotels"
    | "premier_collection"
    | "experiences"
    | "other";
  url: string;
}

export const CALENDAR_DATES_SELECTED = "calendar_dates_selected";

export interface CalendarDatesSelectedProperties {
  funnel:
    | "flights"
    | "cars"
    | "hotels"
    | "premier_collection"
    | "experiences"
    | "other";
  url: string;
  start_date: string;
  end_date: string;
}

export const NETWORK_CALL_FAILED = "network_call_failed";
export const MODAL_ALERT = "modal_alert";
export const MODAL_ALERT_CHOICE = "modal_alert_choice";

export const VIEWED_TAKEOVER = "viewed_takeover";
export const TAPPED_TAKEOVER_CHOICE = "tapped_takeover_choice";

export enum ModalCategoryType {
  FEATURE = "feature",
  TROUBLE = "trouble",
}

export enum ModalScreens {
  CARS_AVAILABILITY = "cars availability",
  CARS_CHECKOUT = "cars checkout",
  CARS_SHOP = "cars shop",
  CARS_SEARCH = "cars search",
  FLIGHTS_CHECKOUT = "flights checkout",
  FLIGHTS_SEARCH = "flights search",
  HOTELS_AVAILABILITY = "hotels availability",
  HOTELS_SHOP = "hotels details",
  HOTELS_CHECKOUT = "hotels checkout",
  HOTELS_SEARCH = "hotels search",
  FLIGHTS_AVAILABILITY = "flights availability",
  DISRUPTION_REBOOKING = "disruption_rebooking",
  MY_TRIPS = "my trips",
  ADD_WATCH_FLIGHT_LIST = "add watch flight list",
  VR_CHECKOUT = "vr checkout",
  VR_DETAILS = "vr details",
  FLIGHTS_SHOP = "flights shop",
  PACKAGES_CHECKOUT = "packages checkout",
  PACKAGES_AVAILABILITY = "packages availability",
  EXPERIENCES_CHECKOUT = "experiences checkout",
  EXPERIENCES_AVAILABILITY = "experiences availability",
  EXPERIENCES_SHOP = "experiences shop",
  PREMIUM_STAYS_LANDING = "premium stays landing",
  PREMIUM_OFFERS_SEARCH = "premium offers search",
  PREMIUM_HOTELS_DATELESS_SEARCH = "premium hotels dateless search",
}

export enum CorpModalScreens {
  ONBOARDING_WELCOME = "onboarding welcome",
  ONBOARDING_LIST = "onboarding list",
  ONBOARDING_DETAILS = "onboarding details",
  ONBOARDING_ADDITIONAL = "onboarding additional",
  ONBOARDING_POLICY = "onboarding policy",
  YOU_ARE_ALL_SET = "you_are_all_set",
}

export enum ModalButtonType {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  CLOSE = "close",
}

export interface ModalAlertProperties extends Cap1HotelCfarRRProperties {
  lob?:
    | "na"
    | "air"
    | "ground"
    | "hotel"
    | "storefront"
    | "vacation rentals"
    | "experiences";
  type: string;
  category: ModalCategoryType;
  screen?: string;
  primary_button: string;
  secondary_button: string;
  modal_subtitle: string;
  modal_title: string;
  agent_title: string;
  agent_subtitle: string;
  step: string;
  funnel:
    | "flights"
    | "cars"
    | "hotels"
    | "premier_collection"
    | "packages"
    | "vacation_rentals"
    | "experiences"
    | "other";
  modal_type?: string;
  is_reshop?: boolean;
}

export interface ModalAlertChoiceProperties extends ModalAlertProperties {
  button_choice: ModalButtonType;
  button?: string;
}

export interface OnboardingModalChoiceProperties
  extends ModalAlertChoiceProperties {
  has_changes: boolean;
}

export const VIEWED_FLIGHT_SHOP_LOADING = "viewed_flight_shop_loading";
export const VIEWED_FLIGHT_BOOK_PROCESSING = "viewed_flight_book_processing";
export const VIEWED_HOTEL_AVAILABILITY_LOADING =
  "viewed_hotel_availability_loading";
export const VIEWED_HOTEL_SHOP_LOADING = "viewed_hotel_shop_loading";
export const VIEWED_HOTEL_BOOK_PROCESSING = "viewed_hotel_book_processing";
export const VIEWED_PC_AVAILABILITY_LOADING = "viewed_pc_availability_loading";
export const VIEWED_PC_SHOP_LOADING = "viewed_pc_shop_loading";
export const VIEWED_PC_BOOK_PROCESSING = "viewed_pc_book_processing";
export const VIEWED_CAR_AVAILABILITY_LOADING =
  "viewed_car_availability_loading";
export const VIEWED_CAR_SHOP_LOADING = "viewed_car_shop_loading";
export const VIEWED_CAR_BOOK_PROCESSING = "viewed_car_book_processing";
export const NO_AVAILABILITY_AIR = "no_availability_air";
export const NO_AVAILABILITY_HOTELS = "no_availability_hotels";
export const NO_AVAILABILITY_CARS = "no_availability_cars";
export const CHARGE_BOOKING_AGENT_FEE = "charge_booking_agent_fee";

export enum SelfServeEvents {
  ClickCancel = "self_serve_click_cancel_button",
  ClickSupport = "self_serve_click_support",
  ClickRedirectToCarrier = "self_serve_redirect_to_carrier",
  ClickSeatsLink = "self_serve_clicked_seats_airline_link",
  ConfirmCancellation = "self_serve_confirm_cancellation",
  RedirectToAirline = "self_serve_redirect_to_airline",
  ResponseFailure = "self_serve_cancel_response_failure",
  ResponseSuccess = "self_serve_cancel_response_success",
  ViewedCancelModal = "self_serve_viewed_cancel_modal",
  ViewedSavedTravelers = "self_serve_saved_travelers_viewlist",
  ClickSavedTravelersAddNew = "self_serve_saved_travelers_addnew",
  ClickSavedTravelersSaveTraveler = "self_serve_saved_travelers_savechanges",
  ViewedExchangeModal = "self_serve_viewed_exchange_modal",
  ViewedExchangeLanding = "self_serve_exchange_landingpage",
  ViewedExchangeShopPage = "self_serve_exchange_shoppage",
  ViewedExchangeReviewPage = "self_serve_exchange_reviewpage",
  ViewedExchangeConfirmPage = "self_serve_exchange_confirmpage",
  ViewedTravelerDetailModal = "self_serve_traveler_detail_view_modal",
  EditTravelerDetailModal = "self_serve_traveler_detail_edit_modal",
  SubmitEditTravelerDetailModal = "self_serve_traveler_detail_submit_change",
  FTCExchangeViewedNoResults = "self_serve_ftcexchange_viewed_no_results",
  ExchangeNotEligible = "self_serve_exchange_not_eligible",
  ExchangeViewedNoResults = "self_serve_exchange_viewed_no_results",
  ExchangePriceQuoteSuccess = "self_serve_exchange_price_quote_success",
  ExchangePriceQuoteFailure = "self_serve_exchange_price_quote_failure",
  ExchangeBookSuccess = "self_serve_exchange_book_success",
  ExchangeBookFailure = "self_serve_exchange_book_failure",
  ExchangeSubmitSuccess = "self_serve_exchange_submitsuccess",
  ExchangeSubmitFailure = "self_serve_exchange_submitfailure",
  ViewedFTCExchangeLanding = "self_serve_ftcexchange_landingpage",
  ViewedFTCExchangeShopPage = "self_serve_ftcexchange_shoppage",
  ViewedFTCExchangeReviewPage = "self_serve_ftcexchange_reviewpage",
  ViewedFTCExchangeConfirmPage = "self_serve_ftcexchange_confirmpage",
  FTCExchangeNotEligible = "self_serve_ftcexchange_not_eligible",
  FTCExchangeSubmitSuccess = "self_serve_ftcexchange_submitsuccess",
  FTCExchangeSubmitFailure = "self_serve_ftcexchange_submitfailure",
  FTCExchangePriceQuoteSuccess = "self_serve_ftc_redeem_price_quote_success",
  FTCExchangePriceQuoteFailure = "self_serve_ftc_redeem_price_quote_failure",
  FTCExchangeBookSuccess = "self_serve_ftc_redeem_book_success",
  FTCExchangeBookFailure = "self_serve_ftc_redeem_book_failure",
  CompletedRequestSelfServe = "completed_request_self_serve",
}

export interface ViewedTravelerDetailModalProperties {
  url: string;
  reservation_booking_id?: string;
}

export interface ViewedSavedTravelersProperties {
  url: string;
  num_traveler_profiles: number;
}

export interface SavedTravelersProperties {
  url: string;
  frequent_flyer_program_added: boolean;
  num_frequent_flyer_programs: number;
}

export enum SelfServeCancelScenario {
  CfarCancel = "cfar_cancel",
}

export const VIEWED_RESEND_EMAIL = "viewed_resend_email";
export const CONFIRM_RESEND_EMAIL = "confirm_resend_email";
export const RESEND_EMAIL_SUBMIT = "resend_email_submit";

export interface ResendEmailProperties {
  screen: ItineraryEnum;
  reservation_booking_id?: string;
}

export interface ResendEmailSubmitProperties extends ResendEmailProperties {
  success: boolean;
}

export interface ChargeBookingAgentFeeProperties {
  agent_booking_fee_amount_usd: number;
  success: boolean;
}

export const BOOKING_FEE_WAIVED = "booking_fee_waived";

export interface BookingFeeWaivedProperties {
  reason: string;
}

export const CLP_MODAL_VIEW = "CLP_modal_view";

export interface CLPModalViewProperties {
  origin: string;
  destination: string;
  origin_country_code: string;
  destination_country_code: string;
  departure_date: string;
  return_date: string;
  trip_type: "round_trip" | "one_way" | "multi_city";
  fare_class:
    | "BasicEconomy"
    | "Economy"
    | "Business"
    | "PremiumEconomy"
    | "First";
}

export const CLP_CLICK = "CLP_click";

export enum CLPClickEnum {
  CHOOSE_ANOTHER_FLIGHT = "Choose another flight",
  CONTINUE = "Continue",
  EXIT = "Exit",
}

export interface CLPClickProperties {
  selected_button: CLPClickEnum;
}

export const CHANGE_FLIGHT = "change_flight";

export const EDIT_TRAVELERS = "edit_travelers";

export const EDIT_CONTACT = "edit_contact";

export const CAP1_VIEW_BOOKING_ADD_ON = "cap1_view_booking_add_on";

export const CFAR_ADD_ON_CHOICE = "cfar_add_on_choice";

export const CHFAR_ADD_ON_CHOICE = "chfar_add_on_choice";

export const DISRUPTION_ADD_ON_CHOICE = "disruption_add_on_choice";

export interface CfarAttachProperties {
  charged_cfar_total?: number;
  charged_cfar_per_pax?: number;
  offered_cfar_per_pax?: number;
  cfar_product_id?: string;
  cfar_quote_id?: string;
  offered_cfar_product_id?: string[];
  attached_cfar_product_id?: string;
  cfar_policy_id?: string;
  offered_cfar_policy_id?: string[];
  attached_cfar_policy_id?: string;
  cfar_coverage_percentage?: number;
  cfar_premium_percentage?: number;
  cfar_choice?: 1 | 0 | "none";
  cancellation_policy?:
    | "non_refundable"
    | "FTC_no_fees"
    | "FTC_fees"
    | "refundable";
  cfar_shop_failed?: boolean;
  success?: boolean;
  is_multi_ticket: boolean;
  cfar_discount_original_premium?: number;
  cfar_discount_percentage?: number;
}

export interface RefundableFaresProperties {
  refundable_flights?: number;
  // now that we are supporting CoMerch
  cfar_choice?: 2 | 1 | 0 | null;
  cfar_fare_choice?: 1 | 0 | null;
  cfar_fare_offered?: 1 | 0 | null;
  rf_knockout_price?: number;
  number_of_rf?: number;
  rf_adult_price_total_usd?: number;
}

export interface TripAndFareClassFactsProperties {
  lob?: string;
  origin?: string;
  advance?: number;
  carrier?: string;
  fare_type?: string;
  destination?: string;
  return_date?: string;
  departure_date?: string;
  total_stops?: number;
  price_per_pax?: number;
  searched_pax_total?: number;
  fare_class?: string;
}

export const TRIPS_LOADING_COMPLETE = "trips_loading_complete";

// CFAR Exercise Events
export const VIEWED_CFAR_OFFER_OPTIONS = "cap1_viewed_cfar_offer_options";
export const TAPPED_CFAR_OFFER_OPTIONS = "cap1_tapped_cfar_offer_options";
export const VIEWED_CFAR_OFFER = "cap1_viewed_cfar_offer";
export const SELF_SERVE_CLICK_SUPPORT = "self_serve_click_support";
export const SELF_SERVE_CONFIRM_CANCELLATION =
  "self_serve_confirm_cancellation";
export const SELF_SERVE_CANCEL_RESPONSE_SUCCESS =
  "self_serve_cancel_response_success";
export const SELF_SERVE_CANCEL_RESPONSE_FAILURE =
  "self_serve_cancel_response_failure";

export enum CfarCancellationOutcome {
  // CfarCash corresponds to one of the following:
  // 1) cash-only option was returned from the backend
  // 2) choice option was returned from the backend and the user chose cash
  CfarCash = "cfar_cash",

  // CfarFtc = CfarCashOrCfarFtc was returned from the backend and the user chose FTC
  CfarFtc = "cfar_ftc",

  // CfarFtcWithPenalty = CfarCashOrCfarFtcWithPenalty was returned from the backend and the user chose FTC
  CfarFtcWithPenalty = "cfar_ftc_with_penalty",

  CfarCashOrCfarFtc = "cfar_cash_or_cfar_ftc",
  CfarCashOrCfarFtcWithPenalty = "cfar_cash_or_cfar_ftc_with_penalty",
  TicketedVoid = "ticketed_void",
  AirlineFullRefund = "airline_full_refund",
  AirlinePartialRefundOrCfarCash = "airline_partial_refund_or_cfar_cash",
  TooCloseToDeparture = "too_close_to_departure",
  ContactCustomerService = "contact_customer_service",
  CancellationPending = "cancellation_pending",
  Unrecognized = "unrecognized", // Response returned is unrecognized
  Error = "error", // Cannot fetch the response successfully
}

export enum CfarOfferChoice {
  CfarCash = "cfar_cash",
  CfarFtc = "cfar_ftc",
  CfarFtcWithPenalty = "cfar_ftc_with_penalty",
  Error = "error",
}

export enum CfarEvents {
  RebookingFeeWaived = "cfar_ftcexchange_rebookfeewaived",
}

export interface Cap1HotelCfarExerciseFactsProperties {
  cancel_scenario?: SelfServeCancelScenario;
  cfar_outcome?: CfarCancellationOutcome;
  total_price_usd?: number;
  price_per_night?: number;
  subtotal_usd?: number;
  base_total_usd?: number;
  charged_cfar_total_usd?: number;
  charged_cfar_per_night?: number;
  rooms_booked?: number;
  cancellation_policy?: HotelCfarCancelScenarioEnum;
  advance?: number;
  days_until_check_in?: number;
  cfar_fee_is_refundable?: boolean;
}

export const getHotelCfarExerciseFactsFromCfarContractAndReservation = (props: {
  reservation: Reservation;
  cfar?: HotelCfarContract;
  cfarCancelScenario?: HotelCfarCancelScenario;
}): Cap1HotelCfarExerciseFactsProperties => {
  const { reservation, cfar, cfarCancelScenario } = props;
  const { pricing, bedTypes, checkInDate, checkOutDate } = reservation;
  const { payNowTotal, subtotal, taxes } = pricing;
  const { premium } = cfar ?? {};
  const basePriceWithTaxes = subtotal.fiat.value + taxes.fiat.value;
  const cfarFee = premium?.amount.fiat.value;
  const nights = dayjs(checkOutDate).diff(checkInDate, "day");

  return {
    cancel_scenario: SelfServeCancelScenario.CfarCancel,
    cfar_outcome: CfarCancellationOutcome.CfarCash,
    // price paid with cfar fee and any other add ons included
    total_price_usd: payNowTotal.fiat.value,
    // price paid with cfar fee included
    price_per_night:
      nights > 0 && cfarFee !== undefined
        ? (basePriceWithTaxes + cfarFee) / nights
        : undefined,
    // price paid without cfar fee or any other add ons
    subtotal_usd: basePriceWithTaxes,
    // price paid without taxes and fees or any other add ons
    base_total_usd: subtotal.fiat.value,
    // cfar fee
    charged_cfar_total_usd: cfarFee,
    // cfar fee per night
    charged_cfar_per_night:
      nights > 0 && cfarFee !== undefined ? cfarFee / nights : undefined,
    rooms_booked: bedTypes.rooms.length,
    cancellation_policy: cfarCancelScenario?.HotelCfarCancelScenario,
    advance: dayjs(reservation.checkInDate).diff(dayjs(cfar?.purchased), "d"),
    days_until_check_in: dayjs(reservation.checkInDate).diff(dayjs(), "d"),
    cfar_fee_is_refundable:
      cfarCancelScenario?.HotelCfarCancelScenario ===
      HotelCfarCancelScenarioEnum.CfarRefundable
        ? !!cfarCancelScenario.shouldRefundCfarPremium
        : undefined,
  };
};

// TRAVEL WALLET EVENTS
export interface TravelWalletOfferTrackingProperties {
  applicable_air?: boolean;
  applicable_cars?: boolean;
  applicable_hotels?: boolean;
  offer_amount_usd?: number;
  offer_expiration_date?: string;
  offer_expiration_days?: number;
  offer_id?: string;
  offer_name?: string;
  offer_type?: string;
  program_id?: string;
}

export interface SharedTravelOfferTrackingProperties {
  has_offer?: boolean;
  offer_count?: number;
  offer_used?: boolean;
  offer_amount_redeemed_usd?: number;
}

export const SELECTED_TRAVEL_OFFER = "selected_offer";

export enum SelectedTravelOfferScreen {
  FLIGHT_SEARCH = "flight_search",
  FLIGHT_SHOP = "flight_shop",
  FLIGHT_REVIEW = "flight_review",
  FLIGHT_CHECKOUT = "flight_checkout",
  HOTEL_SEARCH = "hotel_search",
  HOTEL_SHOP = "hotel_shop",
  HOTEL_CUSTOMIZE = "hotel_customize",
  HOTEL_DETAILS = "hotel_details",
  HOTEL_CHECKOUT = "hotel_checkout",
  CARS_SEARCH = "cars_search",
  CARS_SHOP = "cars_shop",
  CARS_REVIEW = "cars_review",
  CARS_CHECKOUT = "cars_checkout",
  TRAVEL_WALLET_LANDING = "travel_wallet_landing",
  PREMIUM_STAYS_SHOP = "premium_stays_shop",
  PREMIUM_STAYS_CUSTOMIZE = "premium_stays_customize",
  PREMIUM_STAYS_DETAILS = "premium_stays_details",
  PREMIUM_STAYS_CHECKOUT = "premium_stays_checkout",
  PACKAGES_AVAILABILITY = "packages_availability",
  PACKAGES_CHECKOUT = "packages_checkout",
  PACKAGES_HOTEL_SHOP = "packages_hotel_shop",
  PACKAGES_FLIGHT_SHOP = "packages_flight_shop",
  EXPERIENCES_SEARCH = "experiences_search",
  EXPERIENCES_SHOP = "experiences_shop",
  EXPERIENCES_CHECKOUT = "experiences_checkout",
  HOMES_SHOP = "homes_shop",
}

export interface SelectedTravelOfferProperties
  extends SharedTravelOfferTrackingProperties {
  screen: SelectedTravelOfferScreen;
}

export const VIEWED_OFFERS_LIST = "viewed_offers_list";

export const VIEWED_OFFER_FAQ = "viewed_offer_faq";

export const ENGAGED_OFFER_CTA = "engaged_offer_cta";

export const VIEWED_OFFER_BANNER = "viewed_offer_banner";

// Travel sale
export const VIEWED_SALE_PAGE = "viewed_sale_page";

export interface ViewedSalePageTrackingProperties {
  entry_type?: string;
}

export const SALE_FILTER_APPLIED = "sale_filter_applied";

export interface SaleFilterAppliedTrackingProperties {
  filter_type: string;
}

export const VIEWED_SALE_OFFER_MODAL = "viewed_sale_offer_modal";

export interface ViewedSaleOfferModalTrackingProperties {
  location_brand: string;
  funnel: string;
}

export const CLICKED_SALE_OFFER_CTA = "clicked_sale_offer_cta";

export interface ClickedSaleOfferCTATrackingProperties {
  location_brand: string;
  funnel: string;
}

// DP User Preferences

export const DISRUPTION_OPT_IN_EVENT_NAME = "messaging_opt_in";
export const DISRUPTION_SOURCE = "disruption";

export interface DisruptionOptInProperties {
  messaging_source?: string;
  messaging_channel_preference_app_notif?: boolean;
  messaging_channel_preference_sms?: boolean;
}

// DP Exercise Events

export const SELECTED_DISRUPTION = "selected_disruption";
export const VIEWED_DISRUPTION_PAGE = "viewed_disruption_page";
export const MISSED_CONNECTION_REDEEM = "missed_connection_redeem";
export const DELAYS_REDEEM = "delays_redeem";
export const DISRUPTION_REDEEM_CHOICE_REFUND =
  "disruption_redeem_choice_refund";
export const DISRUPTION_REDEEM_CHOICE_REBOOK =
  "disruption_redeem_choice_rebook";
export const VIEWED_REFUND_CHOICE = "viewed_refund_choice";
export const CONFIRM_REFUND_CHOICE = "confirm_refund_choice";
export const CAP1_DISRUPTION_POLICY_EXERCISED =
  "cap1_disruption_policy_exercised";
export const CAP1_VIEWED_REBOOKING_CHOICE = "cap1_viewed_rebooking_choice";
export const CAP1_SELECTED_REBOOKING_CHOICE = "cap1_selected_rebooking_choice";
export const CAP1_VIEWED_REBOOKING_FLIGHT_LIST =
  "cap1_viewed_rebooking_flight_list";
export const CAP1_REBOOKING_VIEWED_SLICE = "cap1_rebooking_viewed_slice";
export const CAP1_REBOOKING_CONFIRMED_SLICE = "cap1_rebooking_confirmed_slice";
export const CAP1_REBOOKING_REVIEW_DETAILS = "cap1_rebooking_review_details";
export const CAP1_REBOOKING_COMPLETE_BUY = "cap1_rebooking_complete_buy";

export interface Cap1DpExerciseFactsProperties {
  active_disruption?:
    | "missed_connection"
    | "delay"
    | "involuntary_cancel"
    | "none";
  advance?: number;
  delay_duration?: number | "none";
  departure_date?: string;
  destination?: string;
  disruption_attached?: number;
  disruption_product?: "missed_connections" | "delays" | "both";
  disruption_rebook_costs?: number;
  disruption_refund_costs?: number;
  itinerary_departure_date?: string;
  itinerary_return_date?: string;
  origin?: string;
  outbound_segments?: number;
  outbound_stops?: number;
  page?: "disruption-page" | "rebook-flight-list";
  product_redeem_choice?: "missed_connection" | "delay";
  rebook_destination?: string;
  rebook_origin?: string;
  return_segments?: number;
  return_stops?: number;
  total_stops?: number;
}

export type PartialFlightsListDpExerciseFacts = Pick<
  Cap1DpExerciseFactsProperties,
  "disruption_attached" | "disruption_product"
>;

export type PartialEligibilityDpExerciseFacts = Pick<
  Cap1DpExerciseFactsProperties,
  "active_disruption" | "delay_duration"
>;

export type PartialDisruptedFlightDpExerciseFacts = Pick<
  Cap1DpExerciseFactsProperties,
  | "departure_date"
  | "itinerary_departure_date"
  | "itinerary_return_date"
  | "advance"
  | "destination"
  | "origin"
  | "outbound_segments"
  | "outbound_stops"
  | "return_segments"
  | "return_stops"
  | "total_stops"
>;

export const getPartialEligibilityDpExerciseFactsPropertiesFromEligibility = (
  eligibility: ExerciseEligibilityV1 | undefined,
): PartialEligibilityDpExerciseFacts => {
  const disruptedSlice = (() => {
    switch (eligibility?.ExerciseEligibilityV1) {
      case ExerciseEligibilityV1Enum.IsEligible:
        return getEligibleSliceFromEligibility(eligibility);
      case ExerciseEligibilityV1Enum.NotEligible:
      default:
        return undefined;
    }
  })();
  const disruptedSegment = disruptedSlice?.slice.segments.find(
    (segment) =>
      segment.EligibilitySegment === EligibilitySegmentEnum.IsEligible,
  );
  const reasons = (() => {
    const disruption = disruptedSegment?.disruption;
    switch (disruption?.FlightDisruption) {
      case FlightDisruptionEnum.IsDisrupted:
        return disruption.reasons;
      default:
        return undefined;
    }
  })();

  if (reasons) {
    const reason = reasons[0];
    switch (reason?.Reason) {
      case FlightDisruptionReasonEnum.Delayed:
        return {
          active_disruption: "delay",
          // note: use dayjs here to convert the duration into seconds; though we might need to know whether we want to add dayjs as a dependency or not.
          delay_duration: reason.inMinutes * 60,
        };
      case FlightDisruptionReasonEnum.MissedConnection:
        return {
          active_disruption: "missed_connection",
          delay_duration: reason.inMinutes * 60,
        };
      case FlightDisruptionReasonEnum.AirlineCancellation:
        return {
          active_disruption: "involuntary_cancel",
          delay_duration: "none",
        };
      default:
        return {
          active_disruption: "none",
          delay_duration: "none",
        };
    }
  }

  return {
    active_disruption: "none",
    delay_duration: "none",
  };
};

export interface Cap1DpOfferFactsProperties {
  disruption_product?: "delays" | "missed_connections";
  disruption_choice?: "0" | "1" | "none";
  disruption_per_pax?: number;
  disruption_policy_id?: string;
  disruption_product_id?: string;
  disruption_total?: number;
  charged_disruption_total_usd?: number;
  charged_disruption_per_pax?: number;
  outbound_segments?: number;
  outbound_stops?: number;
  return_segments?: number;
  return_stops?: number;
  total_stops?: number;
  ancillaries_callstate?: string;
  num_disruption_offers?: number;
}

export type Cap1DisruptionAddOnChoiceProperties = Pick<
  Cap1DpOfferFactsProperties,
  | "disruption_product"
  | "disruption_choice"
  | "disruption_per_pax"
  | "disruption_policy_id"
  | "disruption_product_id"
  | "disruption_total"
  | "outbound_segments"
  | "outbound_stops"
  | "return_segments"
  | "return_stops"
  | "total_stops"
>;

export interface Cap1DpExerciseItineraryFactsProperties {
  success?: boolean;
  exercise_choice?: "refund" | "rebook";
  base_total_usd?: number;
  advance?: number;
  carrier?: string;
  origin?: string;
  destination?: string;
  departure_date?: string;
  return_date?: string;
  itinerary_create_date?: string;
  outbound_local_departure_date?: string;
  outbound_segments?: number;
  outbound_stops?: number;
  return_segments?: number;
  return_stops?: number;
  disruption_new_system_locator?: string;
  disruption_original_agent_locator?: string;
  disruption_original_system_locator?: string;
  disruption_original_trip_cost?: number;
  disruption_policy_id?: string;
  disruption_premium?: number;
  disruption_product_id?: string;
  multi_ticket_type?: string;
  pax_total?: number;
  price_per_pax_usd?: number;
  rebooking_direction?: "outbound" | "return";
  rebooking_flow?: "missed" | "delay" | "disruption";
  rebooking_tool?: boolean;
  subtotal_usd?: number;
  total_price?: number;
  total_price_usd?: number;
  trip_type?: "one_way" | "round_trip";
  total_stops?: number;
}

export type PartialOriginalItineraryDpExerciseItineraryFacts = Pick<
  Cap1DpExerciseItineraryFactsProperties,
  "rebooking_direction" | "rebooking_flow" | "trip_type" | "departure_date"
>;

export type PartialRebookedItineraryDpExerciseItineraryFacts = Pick<
  Cap1DpExerciseItineraryFactsProperties & Cap1DpExerciseFactsProperties,
  | "departure_date"
  | "return_date"
  | "price_per_pax_usd"
  | "rebooking_direction"
  | "rebooking_flow"
  | "rebooking_tool"
  | "subtotal_usd"
  | "total_price"
  | "total_price_usd"
  | "trip_type"
  | "disruption_rebook_costs"
  | "rebook_origin"
  | "rebook_destination"
>;

export type PartialRebookedItineraryDpDisruptionPolicyExercisedFacts = Pick<
  Cap1DpExerciseItineraryFactsProperties & Cap1DpExerciseFactsProperties,
  | "pax_total"
  | "exercise_choice"
  | "base_total_usd"
  | "disruption_new_system_locator"
  | "disruption_original_agent_locator"
  | "disruption_original_system_locator"
  | "disruption_original_trip_cost"
  | "disruption_policy_id"
  | "disruption_premium"
  | "disruption_product_id"
  | "product_redeem_choice"
>;

export type PartialRebookedItineraryDpRebookingCompleteBuyFacts = Pick<
  Cap1DpExerciseItineraryFactsProperties & Cap1DpExerciseFactsProperties,
  | "advance"
  | "carrier"
  | "destination"
  | "origin"
  | "outbound_segments"
  | "outbound_stops"
  | "return_segments"
  | "return_stops"
  | "rebook_origin"
  | "rebook_destination"
>;

export interface Cap1DpExerciseFlightsListFactsProperties {
  flights_shown?: number;
  fares_shown_outbound?: number;
  fares_shown_return?: number;
  rebooking_tool_filter?: "today" | "tomorrow";
  searched_pax_total?: number;
  searched_pax_total_infant_lap?: number;
  searched_pax_total_seated?: number;
  selected_pax_lap_infants?: number;
  selected_pax_seat_infants?: number;
  selected_pax_seated?: number;
  selected_pax_total?: number;
  slice_direction?: string;
  rebook_origin?: string;
  rebook_destination?: string;
}

export type PartialRebookSummaryDpExerciseFlightsListFacts = Pick<
  Cap1DpExerciseFlightsListFactsProperties,
  | "flights_shown"
  | "fares_shown_outbound"
  | "fares_shown_return"
  | "rebooking_tool_filter"
>;

export type PartialPassengerDpExerciseFlightsListFacts = Pick<
  Cap1DpExerciseFlightsListFactsProperties,
  | "searched_pax_total"
  | "searched_pax_total_infant_lap"
  | "searched_pax_total_seated"
  | "selected_pax_lap_infants"
  | "selected_pax_seat_infants"
  | "selected_pax_seated"
  | "selected_pax_total"
>;

// LUXURY OFFER PROMOTION EVENTS
export const VIEWED_LUXURY_OFFER_PROMOTION_BANNER =
  "viewed_luxury_offer_promotion_banner";
export interface ViewedLuxuryOfferPromotionBannerProperties {
  offer_name: string;
}

export const CLICKED_LUXURY_OFFER_PROMOTION_CTA =
  "clicked_luxury_offer_promotion_cta";
export interface ClickedLuxuryOfferPromotionCtaProperties {
  offer_name: string;
  entry_point: string;
}

// LUXURY COMP NIGHT EVENTS
export const VIEWED_COMP_NIGHT_BANNER = "viewed_comp_night_banner";
export interface ViewedCompNightBannerProperties {
  step: ViewedCompNightBannerEntryPoint;
}
export enum ViewedCompNightBannerEntryPoint {
  PREMIUM_STAYS_LANDING = "premium_stays_landing",
  FLIGHT_CONFIRMATION = "flight_confirmation",
  CAR_CONFIRMATION = "car_confirmation",
  WALLET_DRAWER_AIR = "wallet_drawer_air",
  WALLET_DRAWER_HOTELS = "wallet_drawer_hotels",
  WALLET_DRAWER_CARS = "wallet_drawer_cars",
  WALLET_DRAWER_PREMIUM_STAYS = "wallet_drawer_premium_stays",
  WALLET_DRAWER_EXPERIENCES = "wallet_drawer_experiences",
  MY_TRAVEL_CREDITS_OFFERS = "my_travel_credits_offers",
}

export const VIEWED_PREMIUM_OFFER_PAGE = "viewed_premium_offer_page";
export interface ViewedPremiumOfferPageProperties {
  entry_point: ViewedCompNightBannerEntryPoint;
}

export const SEARCHED_PREMIUM_OFFERS = "searched_premium_offers";
export interface SearchedPremiumOffersProperties {
  offer_unlocked_count: number;
  offer_locked_count: number;
  unavailable_count: number;
  is_user_triggered: boolean;
  los: number;
}

export const FILTER_APPLIED_PREMIUM_OFFERS = "filter_applied_premium_offer";
export interface FilterAppliedPremiumOffersProperties {
  filter_type: PremiumOffersFilterType;
  sort_type: string;
}

export enum PremiumOffersFilterType {
  ALL = "All",
  CITY = "City",
  OUTDOORS = "Outdoors",
  WELLNESS = "Wellness",
  ARTS_AND_CULTURE = "Arts & culture",
  BEACH = "Beach",
}

export const SELECTED_HOTEL_FROM_PREMIUM_OFFER_LIST =
  "selected_hotel_from_premium_offer_list";
export enum SelectedHotelOfferState {
  LOCKED = "locked",
  UNLOCKED = "unlocked",
  UNAVAILABLE = "unavailable",
}

export interface SelectedHotelFromPremiumOfferListProperties {
  lodging_id: string;
  collection: string;
  offer_state: SelectedHotelOfferState;
  is_available: boolean;
}

// PC EVENTS

// HOTEL EVENTS
export const LC_ENTRY = "lc_entry";

export const PC_ENTRY = "pc_entry";

export interface PremierCollectionEntryProperties {
  delegated_to: string;
  first_launch: boolean;
  market: string;
  country: string;
  number_of_properties: number;
  number_of_guests: number;
  hotel_advance: string;
  rooms_searched: number;
  has_credits: boolean;
  credit_balance: number;
  vx_statement_credit_balance: number;
  rewards_accounts?: string;
  customer_account_role?: string;
  card_on_file?: boolean;
  is_mega_menu?: boolean;
}

export const VIEWED_PC_LIST = "viewed_pc_list";

export interface ViewedPremierCollectionListProperties
  extends PremierCollectionEntryProperties,
    TravelWalletOfferTrackingProperties,
    SharedTravelOfferTrackingProperties {
  account_type_selected: string;
  account_use_type?: string;
  customer_account_role?: string;
  account_allow_rewards_redemption?: boolean;
  unavailable_hotels: number;
  available_hotels: number;
  nth_night_unlocked_count?: number;
  nth_night_locked_count?: number;
  hotel_details_entry_source?: string;
}

export const VIEWED_UNAVAILABLE_PC_LIST = "viewed_unavailable_pc_list";

export interface ViewedUnavailablePremierCollectionListProperties
  extends ViewedPremierCollectionListProperties {}

export const APPLIED_PC_FILTER = "applied_pc_filter";

export interface AppliedPremierCollectionFilterProperties {
  filter_type: "stars" | "price" | "hotel_name" | "amenities";
}

export const SELECTED_PC_FROM_LIST = "selected_pc_from_list";

export interface SelectedPremierCollectionFromListProperties
  extends ViewedPremierCollectionListProperties {
  is_preferred_cot: boolean;
  nth_night_promo?: string;
  is_available: boolean;
}

export const SELECTED_PC_FROM_MAP = "selected_pc_from_map";

export interface SelectedPremierCollectionFromMapProperties
  extends ViewedPremierCollectionListProperties {
  is_preferred_cot: boolean;
  is_available: boolean;
}

export const SELECTED_LC_FROM_LIST = "selected_lc_from_list";

export interface SelectedLifestyleCollectionFromListProperties
  extends ViewedPremierCollectionListProperties {
  is_available: boolean;
}

export const SELECTED_LC_FROM_MAP = "selected_lc_from_map";

export interface SelectedLifestyleCollectionFromMapProperties
  extends ViewedPremierCollectionListProperties {
  is_available: boolean;
}

export const VIEWED_DATELESS_HOTEL_DETAILS = "viewed_dateless_hotel_details";
export const VIEWED_UNAVAILABLE_DATELESS_HOTEL_DETAILS =
  "viewed_unavailable_dateless_hotel_details";

export const VIEWED_LC_DETAILS = "viewed_lc_details";

export const VIEWED_PC_DETAILS = "viewed_pc_details";

export interface ViewedPremierCollectionDetailsProperties
  extends ViewedPremierCollectionListProperties,
    DatelessSearchProperties,
    PremiumOffersPageProperties {
  account_type_selected: string;
  rooms_shown: number;
  is_preferred_cot: boolean;
  nth_night_promo?: string;
  source?: string;
  vibe_name?: "beach" | "city" | "outdoors" | string;
}

export const SELECT_LC_ROOM = "select_lc_room";

export const SELECT_PC_ROOM = "select_pc_room";

export interface SelectPremierCollectionRoomProperties
  extends ViewedPremierCollectionDetailsProperties {
  room_type: string;
  account_type_selected: string;
}

export const REVIEW_DETAILS_LC_CHECKOUT = "review_details_lc_checkout";

export const REVIEW_DETAILS_PC_CHECKOUT = "review_details_pc_checkout";

export interface ReviewDetailsPremierCollectionCheckoutProperties
  extends ViewedPremierCollectionListProperties {
  lodging_name: string;
  market: string;
  chain: string;
  brand: string;
  length_of_stay: number;
  sell_price_excluding_taxes_and_fees_usd: number;
  owed_now_excluding_taxes_and_fees: number;
  owed_now_taxes_and_fees: number;
  owed_at_checkin_taxes_and_fees: number;
  pay_later_excluding_taxes_and_fees: number;
  pay_later_taxes_and_fees: number;
  pay_later_fees: number;
  total_discount: number;
  refundability: "non_refundable" | "free_cancellation";
  supply_revenue_usd: number;
  check_in_date: string;
  check_out_date: string;
  supplier: string;
  hotel_advance: string;
  provider_type: "direct" | "retail" | "wholesale";
  adr: string;
  is_preferred_cot: boolean;
}

export const PC_SAVE_AND_CONTINUE_CLICKED = "pc_save_and_continue_clicked";

export const PRICE_QUOTE_LC = "price_quote_lc";

export const PRICE_QUOTE_PC = "price_quote_pc";

export interface PriceQuotePremierCollectionProperties
  extends ReviewDetailsPremierCollectionCheckoutProperties {
  success: boolean;
  booking_session_id: string;
  lodging_row_index?: number;
  x_price_difference: number;
  has_restricted_credits?: boolean;
  restricted_credit_balance?: number;
}

export const COMPLETE_BUY_LC = "complete_buy_lc";

export interface CompleteBuyLifestyleCollectionProperties
  extends CompleteBuyPremierCollectionProperties {}

export const COMPLETE_BUY_PC = "complete_buy_pc";

export interface CompleteBuyPremierCollectionProperties
  extends ReviewDetailsPremierCollectionCheckoutProperties,
    DatelessSearchProperties,
    PremiumOffersPageProperties {
  success: boolean;
  total_rewards_amount_usd: number;
  total_card_amount_usd: number;
  card_product_used: string;
  rewards_product_used: string;
  rewards_currency: string; // ie. “Miles”, “Cash”, “Points”
  agent_booking_fee_amount_usd: number;
  booking_session_id: string;
  reservation_id: string;
  lodging_row_index?: number;
  is_recommended?: boolean;
  rooms_booked: number;
  credit_redeemed: boolean;
  credit_amt_redeemed: number;
  restricted_credit_redeemed: boolean;
  restricted_credit_amt_redeemed: number;
  is_vcn: boolean;
  nth_night_promo?: string;
  x_price_difference: number;
  payment_schedule: string;
  booked_self_name_match: boolean;
}

export const PAYMENT_SCHEDULE_SELECTED = "payment_schedule_selected";

export const VIEWED_PAYMENT_SCHEDULE_MODAL = "viewed_payment_schedule_modal";

export interface DatelessSearchProperties {
  is_dateless?: boolean;
}

export interface PremiumOffersPageProperties {
  is_from_premium_offers_page?: boolean;
}

// MEGAMENU EVENTS
export const MEGA_MENU_ENTRY = "mega_menu_entry";
export const MEGA_MENU_SELECTED_NODE = "megu_menu_selected_node";

export enum MegaMenuNodeType {
  ROOT = "root",
  SUBSECTION = "subsection",
  LEAF = "leaf",
}

export interface MegaMenuEntryProperties {
  is_expanded?: boolean;
}
export interface MegaMenuSelectedNodeProperties {
  label: string;
  node_type: MegaMenuNodeType;
}

// VR EVENTS
export const VR_TOGGLE_SELECTED = "vr_toggle_selected";

export const VR_ENTRY = "vr_entry";

export const VIEWED_PREMIUM_SEARCH = "viewed_premium_search";

export interface VacationRentalEntryProperties {
  adults_count: number;
  advance?: number;
  check_in_date?: string;
  check_out_date?: string;
  children_count: number;
  pets_count?: number;
  delegated_to: string;
  destination_city?: string;
  destination_country?: string;
  destination_name?: string;
  google_place_id?: string;
  market?: string;
  number_of_guests?: number;
  los?: number;
  first_launch: boolean;
  has_credits: boolean;
  credit_balance: number;
  vx_statement_credit_balance: number;
  rewards_accounts?: string;
  customer_account_role?: string;
  card_on_file?: boolean;
}

export const VIEWED_VR_LIST = "viewed_vr_list";

export interface ViewedVacationRentalListProperties
  extends VacationRentalEntryProperties,
    TravelWalletOfferTrackingProperties,
    SharedTravelOfferTrackingProperties {
  market_selection_name?: string;
  account_type_selected: string;
  account_use_type?: string;
  account_allow_rewards_redemption?: boolean;
  available_vr?: number;
  pc_shown?: number;
  lc_shown?: number;
  rooms_searched?: number;
  searched_pax_total?: number;
  pet_searched?: boolean;
}

export const SELECTED_VR_FROM_LIST = "selected_vr_from_list";

export interface SelectedVacationRentalFromListProperties
  extends ViewedVacationRentalListProperties {
  vr_type: string;
  vr_occupancy: number;
  vr_pet_friendly: boolean;
  vr_market: string;
  vr_price?: number;
}

export const SELECTED_VR_FROM_MAP = "selected_vr_from_map";

export interface SelectedVacationRentalFromMapProperties
  extends SelectedVacationRentalFromListProperties {}

export const VIEWED_VR_DETAILS = "viewed_vr_details";

export interface ViewedVacationRentalDetailsProperties
  extends ViewedVacationRentalListProperties {
  adr?: number;
  commission_usd?: number;
  // credits_amount_usd?: number;
  // discount_hopper_funded_usd?: number;
  // discount_host_funded_usd?: number;
  // discount_type?: string;
  // discount_usd?: number;
  home_city?: string;
  home_country?: string;
  home_host_name?: string;
  home_id?: string;
  home_state?: string;
  host_service_fee_usd?: number;
  market_id?: string;
  market_name?: string;
  markup_percentage?: number;
  markup_usd?: number;
  net_rate_excluding_taxes_fees_usd?: number;
  number_of_photos?: number;
  taxes_and_fees?: number;
  total_price?: number;
  product_type?: string;
  free_cancellation_shown?: boolean;
  pets_searched?: number;
  google_place_id?: string;
  destination_name?: string;
  lodging_row_index?: number;
  rooms_searched?: number;
  details_entry_source?: string;
  sort_order?: string;
  has_reviews?: boolean;
  reviews_score?: number;
  in_policy?: boolean;
  is_boosted?: boolean;
}

export const VIEWED_VR_DETAILS_PHOTO_GALLERY =
  "viewed_vr_details_photo_gallery";
export const VR_DETAILS_PHOTO_GALLERY_IMAGE_LOAD_ERROR =
  "vr_details_photo_gallery_image_load_error";

export const REVIEW_DETAILS_VR_CHECKOUT = "review_details_vr_checkout";

export const COMPLETE_BUY_VR = "complete_buy_vr";

export interface CompleteBuyVacationRentalProperties
  extends ViewedVacationRentalDetailsProperties {
  reservation_id?: string;
  total_card_amount_usd?: number;
  total_rewards_amount_usd?: number;
  success: boolean;
  error_details?: string;
  error_displayed?: string;
  tab_view_on_availability?: TabViewEnum;
  funnel_of_origin?: string;
  card_product_used: string;
  rewards_product_used: string;
  credit_redeemed?: boolean;
  credit_amt_redeemed?: number;
  booked_self_name_match: boolean;
}

// CREDITS TRACKING

export const VIEWED_CREDIT_BANNER = "viewed_credit_banner";

export const ENGAGED_CREDIT_CTA = "engaged_credit_cta";

export const VIEW_CREDIT_HISTORY = "view_credit_history";

export const VIEW_TRAVEL_WALLET = "view_travel_wallet";

export const VIEWED_CREDIT_BENEFITS_MODAL = "viewed_credits_benefits_modal";

// HOTEL CFAR
export interface Cap1HotelCfarOfferFactsProperties {
  cfar_premium_percentage?: number;
  cfar_premium_usd?: number;
  cfar_standalone_premium_percentage?: number;
  cfar_standalone_premium_usd?: number;
  cfar_rr_premium_percentage?: number;
  cfar_rr_premium_usd?: number;
  cfar_pricing_description?: string;
  cfar_rr_price_total_usd?: number;
  cfar_rr_hotel_choice?: 1 | 0 | "none";
  cfar_choice?: 1 | 0 | "none";
  cfar_fee_is_refundable?: boolean;
  cfar_added_from_modal?: boolean;
}

export type Cap1HotelCfarPremiumProperties = Pick<
  Cap1HotelCfarOfferFactsProperties,
  | "cfar_standalone_premium_usd"
  | "cfar_standalone_premium_percentage"
  | "cfar_rr_premium_usd"
  | "cfar_rr_premium_percentage"
>;

export type Cap1HotelCfarRRProperties = Pick<
  Cap1HotelCfarOfferFactsProperties,
  | "cfar_rr_hotel_choice"
  | "cfar_choice"
  | "cfar_fee_is_refundable"
  | "cfar_premium_usd"
>;

interface IRefundableRoomProductWithCfarChoices
  extends Pick<
    Cap1HotelCfarOfferFactsProperties,
    "cfar_rr_hotel_choice" | "cfar_choice"
  > {
  refundableRoomProduct: RoomProductWithTransformedIndexes | undefined;
}

const getHotelCfarOfferFactsFromRefundableRoomProduct = ({
  refundableRoomProduct,
  cfar_rr_hotel_choice,
  cfar_choice,
}: IRefundableRoomProductWithCfarChoices): Cap1HotelCfarOfferFactsProperties => {
  if (
    !!refundableRoomProduct?.roomProductVariant &&
    refundableRoomProduct.roomProductVariant.variant ===
      RoomProductType.RefundableRoom
  ) {
    const { totalDiscountAware, total } = refundableRoomProduct;
    const { premiumPrices, pricingDescription, additionalInfo } =
      refundableRoomProduct.roomProductVariant;
    const isCfarFeeRefundable = additionalInfo?.shouldRefundCfarPremium;

    return {
      cfar_premium_percentage:
        total.fiat.value > 0
          ? Math.round((premiumPrices.fiat.value * 100) / total.fiat.value)
          : undefined,
      cfar_premium_usd: premiumPrices.fiat.value,
      cfar_pricing_description: pricingDescription,
      cfar_rr_price_total_usd:
        totalDiscountAware?.priceWithUnmanagedDiscounts.fiat.value ??
        total.fiat.value,
      cfar_rr_hotel_choice,
      cfar_choice,
      cfar_fee_is_refundable: isCfarFeeRefundable,
    };
  }

  return { cfar_rr_hotel_choice, cfar_choice };
};

interface IHotelCfarQuoteWithCfarChoices
  extends Pick<
    Cap1HotelCfarOfferFactsProperties,
    "cfar_rr_hotel_choice" | "cfar_choice"
  > {
  roomProduct: RoomProduct | null;
  hotelCfarQuote: HotelCfarQuote | null;
  additionalInfo: HotelCfarQuoteAdditionalInfo | null;
  hasAddedCfarFromModal?: boolean;
}

const getHotelCfarOfferFactsFromHotelCfarQuote = ({
  roomProduct,
  hotelCfarQuote,
  additionalInfo,
  cfar_rr_hotel_choice,
  cfar_choice,
  hasAddedCfarFromModal,
}: IHotelCfarQuoteWithCfarChoices): Cap1HotelCfarOfferFactsProperties => {
  const isCfarFeeRefundable = additionalInfo?.shouldRefundCfarPremium;

  if (!roomProduct) {
    return {
      cfar_rr_hotel_choice,
      cfar_choice,
      cfar_fee_is_refundable: isCfarFeeRefundable,
    };
  }

  if (hotelCfarQuote) {
    const { total } = roomProduct;
    const { premiumPrices, pricingDescription } = hotelCfarQuote;

    return {
      cfar_premium_percentage:
        total.fiat.value > 0
          ? Math.round((premiumPrices.fiat.value * 100) / total.fiat.value)
          : undefined,
      cfar_premium_usd: premiumPrices.fiat.value,
      cfar_pricing_description: pricingDescription,
      cfar_rr_hotel_choice,
      cfar_choice,
      cfar_fee_is_refundable: isCfarFeeRefundable,
      cfar_added_from_modal: hasAddedCfarFromModal,
    };
  }

  return {
    cfar_rr_hotel_choice,
    cfar_choice,
    cfar_fee_is_refundable: isCfarFeeRefundable,
  };
};

export type PartialViewedHotelDetailsHotelCfarOfferFacts = Pick<
  Cap1HotelCfarOfferFactsProperties,
  | "cfar_premium_percentage"
  | "cfar_premium_usd"
  | "cfar_pricing_description"
  | "cfar_rr_price_total_usd"
>;

export const getViewedHotelDetailsHotelCfarOfferFacts = (
  refundableRoomProduct: RoomProductWithTransformedIndexes | undefined,
): PartialViewedHotelDetailsHotelCfarOfferFacts => {
  const hotelCfarOfferFacts = getHotelCfarOfferFactsFromRefundableRoomProduct({
    refundableRoomProduct,
  });
  return {
    cfar_premium_percentage: hotelCfarOfferFacts.cfar_premium_percentage,
    cfar_premium_usd: hotelCfarOfferFacts.cfar_premium_usd,
    cfar_pricing_description: hotelCfarOfferFacts.cfar_pricing_description,
    cfar_rr_price_total_usd: hotelCfarOfferFacts.cfar_rr_price_total_usd,
  };
};

export type PartialSelectHotelRoomHotelCfarOfferFacts =
  Cap1HotelCfarOfferFactsProperties;

export const getSelectHotelRoomHotelCfarOfferFacts = (
  props: IRefundableRoomProductWithCfarChoices,
): PartialSelectHotelRoomHotelCfarOfferFacts => {
  return getHotelCfarOfferFactsFromRefundableRoomProduct(props);
};

export const CAP1_HOTEL_VIEWED_ADD_ON = "cap1_hotel_viewed_add_on";

export type HotelViewedAddOnHotelCfarOfferFacts = Pick<
  Cap1HotelCfarOfferFactsProperties,
  | "cfar_premium_percentage"
  | "cfar_premium_usd"
  | "cfar_pricing_description"
  | "cfar_fee_is_refundable"
>;

export const getHotelViewedAddOnHotelCfarOfferFacts = (
  roomProduct: RoomProduct | null,
  hotelCfarQuote: HotelCfarQuote | null,
  additionalInfo: HotelCfarQuoteAdditionalInfo | null,
): HotelViewedAddOnHotelCfarOfferFacts => {
  const hotelCfarOfferFacts = getHotelCfarOfferFactsFromHotelCfarQuote({
    roomProduct,
    hotelCfarQuote,
    additionalInfo,
  });
  return {
    cfar_premium_percentage: hotelCfarOfferFacts.cfar_premium_percentage,
    cfar_premium_usd: hotelCfarOfferFacts.cfar_premium_usd,
    cfar_pricing_description: hotelCfarOfferFacts.cfar_pricing_description,
    cfar_fee_is_refundable: hotelCfarOfferFacts.cfar_fee_is_refundable,
  };
};

export const CAP1_HOTEL_CFAR_ADD_ON_CHOICE = "cap1_hotel_cfar_add_on_choice";

export type HotelCfarAddOnChoiceHotelCfarOfferFacts = Pick<
  Cap1HotelCfarOfferFactsProperties,
  | "cfar_premium_percentage"
  | "cfar_premium_usd"
  | "cfar_pricing_description"
  | "cfar_choice"
  | "cfar_rr_hotel_choice"
  | "cfar_fee_is_refundable"
>;

export const getHotelCfarAddOnChoiceHotelCfarOfferFacts = (
  props: IHotelCfarQuoteWithCfarChoices,
): HotelCfarAddOnChoiceHotelCfarOfferFacts => {
  const hotelCfarOfferFacts = getHotelCfarOfferFactsFromHotelCfarQuote(props);
  return {
    cfar_premium_percentage: hotelCfarOfferFacts.cfar_premium_percentage,
    cfar_premium_usd: hotelCfarOfferFacts.cfar_premium_usd,
    cfar_pricing_description: hotelCfarOfferFacts.cfar_pricing_description,
    cfar_choice: hotelCfarOfferFacts.cfar_choice,
    cfar_rr_hotel_choice: hotelCfarOfferFacts.cfar_rr_hotel_choice,
    cfar_fee_is_refundable: hotelCfarOfferFacts.cfar_fee_is_refundable,
  };
};

export type PartialReviewDetailsHotelCheckoutHotelCfarOfferFacts = Pick<
  Cap1HotelCfarOfferFactsProperties,
  | "cfar_premium_percentage"
  | "cfar_premium_usd"
  | "cfar_pricing_description"
  | "cfar_choice"
  | "cfar_rr_hotel_choice"
  | "cfar_fee_is_refundable"
>;

export const getReviewDetailsHotelCheckoutHotelCfarOfferFacts = (
  props: IHotelCfarQuoteWithCfarChoices,
): PartialReviewDetailsHotelCheckoutHotelCfarOfferFacts => {
  const hotelCfarOfferFacts = getHotelCfarOfferFactsFromHotelCfarQuote(props);
  return {
    cfar_premium_percentage: hotelCfarOfferFacts.cfar_premium_percentage,
    cfar_premium_usd: hotelCfarOfferFacts.cfar_premium_usd,
    cfar_pricing_description: hotelCfarOfferFacts.cfar_pricing_description,
    cfar_choice: hotelCfarOfferFacts.cfar_choice,
    cfar_rr_hotel_choice: hotelCfarOfferFacts.cfar_rr_hotel_choice,
    cfar_fee_is_refundable: hotelCfarOfferFacts.cfar_fee_is_refundable,
  };
};

export type PartialCompleteBuyHotelHotelCfarOfferFacts = Pick<
  Cap1HotelCfarOfferFactsProperties,
  | "cfar_premium_percentage"
  | "cfar_premium_usd"
  | "cfar_pricing_description"
  | "cfar_choice"
  | "cfar_rr_hotel_choice"
  | "cfar_fee_is_refundable"
  | "cfar_added_from_modal"
>;

export const getCompleteBuyHotelHotelCfarOfferFacts = (
  props: IHotelCfarQuoteWithCfarChoices,
): PartialCompleteBuyHotelHotelCfarOfferFacts => {
  const hotelCfarOfferFacts = getHotelCfarOfferFactsFromHotelCfarQuote(props);
  return {
    cfar_premium_percentage: hotelCfarOfferFacts.cfar_premium_percentage,
    cfar_premium_usd: hotelCfarOfferFacts.cfar_premium_usd,
    cfar_pricing_description: hotelCfarOfferFacts.cfar_pricing_description,
    cfar_choice: hotelCfarOfferFacts.cfar_choice,
    cfar_rr_hotel_choice: hotelCfarOfferFacts.cfar_rr_hotel_choice,
    cfar_fee_is_refundable: hotelCfarOfferFacts.cfar_fee_is_refundable,
    cfar_added_from_modal: hotelCfarOfferFacts.cfar_added_from_modal,
  };
};
export const PRODUCT_FEED_LOADING = "product_feed_loading";

export interface ProductFeedLoadingProperties {
  feed_type: "cross_sell_hotels" | "recently_viewed";
  feed_placement: "mytrips" | "homepage" | "flight_confirmation";
}

export const PRODUCT_FEED_VIEWED_EVENT = "product_feed_viewed";

export const PRODUCT_FEED_RENDERED_EVENT = "product_feed_rendered";

export type ProductTileType =
  | "cross_sell_hotel"
  | "cross_sell_air"
  | "cross_sell_activity"
  | "cross_sell_car"
  | "recently_viewed_hotel"
  | "recently_searched_hotel"
  | "recently_searched_air"
  | "recently_searched_car"
  | "recently_searched_package"
  | "recently_searched_experience";

export interface ProductFeedViewedProperties {
  feed_type: "cross_sell" | "recently_viewed";
  feed_placement: "my_trips" | "homepage" | "booking_confirmation";
  feed_sub_placement?: "above_fold" | "mid_fold" | "below_fold";
  tile_type: ProductTileType | ProductTileType[];
  origin_product_type?: "air" | "stay" | "activity" | "unknown";
  click_target?:
    | "view_all"
    | "carousel_card"
    | "carousel_next "
    | "carousel_prev";
  tile_index?: number;
  channel?: "Web" | "Mobile" | "App";
  latency?: number;
}

export const PRODUCT_FEED_TILE_TAPPED = "prodfeed_tile_tapped";

export interface ProductFeedTileTappedProperties {
  feed_type: "cross_sell" | "recently_viewed";
  tile_type: ProductTileType;
  feed_placement: "homepage" | "booking_confirmation" | "my_trips";
  feed_sub_placement?: "above_fold" | "mid_fold" | "below_fold";
  origin_product_type?: "air" | "stay" | "activity" | "unknown";
  click_target?:
    | "view_all"
    | "carousel_card"
    | "carousel_next"
    | "carousel_prev";
  tile_index?: number;
  channel?: "Web" | "Mobile" | "App";
}

export const PRODUCT_FEED_NOT_RENDERED_EVENT = "product_feed_not_rendered";

export type ProductFeedNotRenderedReason =
  | "no_booked_itinerary"
  | "autocomplete_no_location"
  | "already_has_hotel"
  | "no_next_page_token"
  | "empty_availability"
  | "saga_exception"
  | "all_unavailable_filtered"
  | "all_premier_filtered"
  | "no_rewards_account"
  | "no_trip_details"
  | "prebooking_input_null";

export interface ProductFeedNotRenderedProperties {
  reason: ProductFeedNotRenderedReason;
  feed_type: "cross_sell";
  feed_placement: "booking_confirmation";
  feed_sub_placement: "below_fold";
  tile_type: "cross_sell_hotel";
  origin_product_type: "air";
  channel?: "Web" | "Mobile" | "App";
}

export const VIEWED_MISSED_CONNECTION_GUARANTEE_MODAL =
  "viewed_missed_connection_guarantee_modal";

export const VIEWED_SELF_TRANSFER_MODAL = "viewed_self_transfer_modal";

export const VIEWED_VI_BAGGAGE_WARNING = "viewed_vi_baggage_warning";
export const CONFIRMED_VI_BAGGAGE_WARNING = "confirmed_vi_baggage_warning";
export const DECLINED_VI_BAGGAGE_WARNING = "declined_vi_baggage_warning";
export const EXPANDED_FLIGHT_DETAILS = "expanded_flight_details";
export const VIEWED_FARE_DETAILS_BREAKDOWN = "viewed_fare_details_breakdown";

export const ALL_FILTERS_MODAL_CARS_EVENT = "all_filters_modal_cars";

export interface CarsFilterEventProperties {
  car_type?: string;
  passengers?: string;
  bags?: string;
  pickup_location?: string;
  rental_company?: string;
  price?: number;
  cancellation?: string;
}

export interface ExpandedFlightDetailsProperties {
  slice_direction: "outbound" | "return";
  // Should be set to multiprovider for Hopper VI and HFv2 flights
  system_local_provider: string;
  multi_ticket_type: string;
}

export interface ViewedFareDetailsBreakdownProperties {
  entry_point: string;
  multi_ticket_type: string;
}

export interface VirtualInterlineModalProperties {
  entry_point: string;
}

export enum VirtualInterlineEntryPoint {
  Outbound = "outbound",
  Return = "return",
  Details = "itinerary_details",
  TripDetails = "trip_details",
}

export const SELECT_EDIT_TRAVELERS = "select_edit_travelers";

export interface SelectEditTravelersProperties {
  source: string;
}

export const VIEW_BOOKING_ADD_ON_POST_BOOKING =
  "cap1_view_booking_add_on_post_booking";

export const SELECTED_POST_BOOKING_CFAR_OFFER =
  "cap1_selected_post_booking_cfar_offer";

export const REVIEW_DETAILS_CFAR_POST_BOOKING =
  "cap1_review_details_fintech_checkout";

export const DISRUPTION_ADD_ON_CHOICE_POST_BOOKING =
  "cap1_disruption_add_on_choice_post_booking";

export const DISRUPTION_POST_BOOKING_COMPLETE_BUY =
  "cap1_disruption_post_booking_complete_buy";

export const CFAR_POST_BOOKING_COMPLETE_BUY =
  "cap1_cfar_post_booking_complete_buy";

export type PostBookingScreen =
  | "air_homepage"
  | "hotel_homepage"
  | "my_trips_homepage";

export const getPostBookingScreen = (locationSearch: string) => {
  const screens: PostBookingScreen[] = [
    "air_homepage",
    "hotel_homepage",
    "my_trips_homepage",
  ];

  const referenceScreenString = new URLSearchParams(locationSearch).get(
    "referenceScreen",
  );

  return screens.find((str) => str == referenceScreenString) ?? "air_homepage";
};

export const postBookingViewTakeoverPropteries = (
  screen: PostBookingScreen,
  takeover_display_count: number,
  trackingProperties: PostBookingTrackingProperties,
) => {
  return {
    eventName: VIEW_BOOKING_ADD_ON_POST_BOOKING,
    properties: {
      screen: screen,
      takeover_display_count: takeover_display_count,
      ...trackingProperties,
    },
  };
};

export const postBookingViewCheckoutScreenProperties = (
  screen: PostBookingScreen,
  trackingProperties: PostBookingTrackingProperties,
  offerDeeplink?: string,
) => {
  return {
    eventName: VIEW_BOOKING_ADD_ON_POST_BOOKING,
    properties: {
      referring_screen: screen,
      offer_deeplink: offerDeeplink,
      screen: "fda_postbooking_checkout",
      ...trackingProperties,
    },
  };
};

export const postBookingChoiceTappedProperties = (
  screen: PostBookingScreen,
  disruption_choice: 0 | 1,
  trackingProperties: PostBookingTrackingProperties,
  offerDeeplink?: string,
) => {
  return {
    eventName: DISRUPTION_ADD_ON_CHOICE_POST_BOOKING,
    properties: {
      screen: screen,
      offer_deeplink: offerDeeplink,
      disruption_choice: disruption_choice,
      ...trackingProperties,
    },
  };
};

export const postBookingPurchaseCompleteProperties = (args: {
  offerDeeplink?: string;
  screen: PostBookingScreen;
  trackingProperties: PostBookingTrackingProperties;
}) => {
  return {
    eventName: DISRUPTION_POST_BOOKING_COMPLETE_BUY,
    properties: {
      referring_screen: args.screen,
      offer_deeplink: args.offerDeeplink,
      screen: "fda_postbooking_checkout",
      disruption_choice: 1,
      ...args.trackingProperties,
    },
  };
};

// Customer profile

export const CUSTOMER_PROFILE_ENTRY = "customer_profile_entry";

export interface CustomerProfileEntryProperties {
  profile_section:
    | "travelers"
    | "favorites"
    | "hotel_preferences"
    | "flight_preferences"
    | "payment_information"
    | "contact_information";
}

export const APPLIED_PREFERENCES = "applied_preferences";

export interface AppliedPreferencesProperties {
  preferences_type: "hotels" | "flights";
  location: "search" | "hotel_list" | "flight_list";
}

export const CLEAR_ALL_FILTERS = "clear_all_filters";

export const CLICKED_ADD_TRAVELER = "clicked_add_traveler";

export interface ClickedAddTravelerProperties {
  entry_type: "customer_profile" | "checkout";
  funnel?: "packages" | "hotels" | "flights" | "cars";
}

export const SAVED_NEW_TRAVELER = "saved_new_traveler";

export interface SavedNewTravelerProperties {
  entry_type: "customer_profile" | "checkout";
  car_loyalty_program_added?: boolean;
  car_loyalty_programs_in_profile?: string[];
}

export const SAVED_FLIGHT_PREFERENCES = "saved_flight_preferences";

export const SAVED_HOTEL_PREFERENCES = "saved_hotel_preferences";

export const NEW_PAYMENT_METHOD_ADDED = "new_payment_method_added";

export interface NewPaymentMethodAddedProperties {
  entry_type: "customer_profile" | "checkout";
}

// Corporate Travel
export const VIEWED_POLICY_MODAL = "viewed_policy_modal";
export const VIEWED_POLICY_DESCRIPTOR = "viewed_policy_descriptor";
export const POLICY_MODAL = "policy_modal";
export const POLICY_DESCRIPTOR = "policy_descriptor";

export enum PolicyDescriptorEntryPoints {
  FLIGHTS_LIST = "flights_list",
  FLIGHTS_LIST_EXPANDED = "flights_list_expanded",
  FLIGHTS_LIST_RETURN = "flights_list_return",
  FLIGHTS_LIST_RETURN_EXPANDED = "flights_list_return_expanded",
  HOTELS_LIST = "hotels_list",
  HOTELS_ROOM_DETAILS = "hotels_room_details",
  CARS_LIST = "cars_list",
}

export const VIEWED_TRAVELER_DETAILS = "viewed_traveler_details";

export interface SelectEditTravelersProperties {
  source: string;
}

export interface ViewedTravelerDetailsProperties {
  num_traveler_profiles: number;
}

export const CREATED_TRAVELER_PROFILE = "created_traveler_profile";

export const UPDATED_TRAVELER_PROFILE = "updated_traveler_profile";

export interface TravelerProfileProperties {
  source?: string;
  entry_point?: string;
  private?: boolean;
  frequent_flyer_added: boolean;
  num_frequent_flyer_programs: number;
  frequent_flyer_programs_added?: string[];
  hotel_loyalty_program_added: boolean;
  num_hotel_loyalty_programs: number;
  hotel_loyalty_programs_added?: string[];
  hotel_loyalty_programs_removed?: string[];
  car_loyalty_program_added: boolean;
  car_loyalty_programs_in_profile?: string[];
  num_car_loyalty_programs_added?: number;
  num_car_loyalty_programs_removed?: number;
  car_loyalty_programs_removed?: string[];
  is_default_profile?: boolean;
  has_personal_details?: boolean;
  has_contact_email?: boolean;
  has_tsa_precheck?: boolean;
  has_drivers_license?: boolean;
  has_passport?: boolean;
}

export const PRICE_MATCH_CLICKED = "price_match_clicked";

export const PORTAL_SWITCHED = "portal_switched";

export interface PortalSwitchedProperties {
  portal_selected: PortalTypes;
  current_portal: PortalTypes;
}

export const VIEWED_ITEMIZED_RECEIPT = "viewed_itemized_receipt";

export const PRINTED_ITEMIZED_RECEIPT = "printed_itemized_receipt";

export interface ItemizedReceiptEventsProperties {
  trip_id: string;
  active_tab: string;
  travel_type: string;
  reservation_booking_id?: string;
}

export const VIEWED_LODGING_RESERVATION_IN_LIST =
  "viewed_lodging_reservation_in_list";

export interface ViewedLodgingReservationInListProperties {
  reservation_booking_id?: string;
  lodging_id?: string;
  lodging_name?: string;
  check_in_date?: string;
  check_out_date?: string;
  hotel_confirmation_number_status?: string;
  active_tab?: string;
}

export const VIEWED_TRAVEL_OFFER = "viewed_travel_offer";
export const VIEWED_CORP_RATE_DESCRIPTOR = "viewed_corporate_rate_descriptor";

export enum ViewedCorpRateDescriptorEntryPoints {
  HOTELS_LIST_HEADER = "hotels_list_header",
  HOTELS_LIST_CARD = "hotels_list_card",
  HOTELS_LIST_MAP = "hotels_list_map",
  HOTELS_ROOM_DETAILS = "hotels_room_details",
  HOTELS_CHECKOUT = "hotels_checkout",
}

export const CORP_SWITCH_ACCOUNT = "switch_account";

export const CORP_SWITCH_ACCOUNT_CONTINUE = "switch_account_continue";

export const CORP_BUSINESS_LOYALTY_ADDED = "business_loyalty_added";

export interface CorpBusinessLoyaltyAddedProperties {
  program_name: string;
  source: "checkout";
}

export const VXB_ACKNOWLEDGED = "vxb_acknowledged";
export const TAKEOVER_NOTICE_VIEWED = "takeover_notice_viewed";
export const HOTEL_BOOK_AGAIN = "hotel_book_again";

export enum HotelBookAgainSourceEnum {
  MyTrips = "my_trips",
  Confirmation = "confirmation",
}

export interface HotelBookAgainProperties {
  source: HotelBookAgainSourceEnum;
}

// PACKAGES EVENTS
export const PACKAGE_ENTRY = "package_entry";
export const VIEWED_PACKAGES_SEARCH = "viewed_packages_search";
export const VIEWED_PACKAGES_LIST = "viewed_packages_list";
export const APPLIED_PACKAGES_FILTER = "applied_packages_filter";
export const SELECTED_PACKAGE_HOTEL_FROM_LIST =
  "selected_package_hotel_from_list";
export const SELECTED_PACKAGE_HOTEL_FROM_MAP =
  "selected_package_hotel_from_map";
export const VIEWED_PACKAGE_LODGING_IN_LIST = "viewed_package_lodging_in_list";
export const SELECTED_UNAVAILABLE_PACKAGE_HOTEL_FROM_LIST =
  "selected_unavailable_package_hotel_from_list";
export const COMPLETE_BUY_PACKAGE = "complete_buy_package";
export const PRICE_QUOTE_PACKAGE = "price_quote_package";
export const VIEWED_PACKAGE_CHECKOUT = "viewed_package_checkout";
export const VIEWED_PACKAGE_HOTEL_SHOP_LOADING =
  "viewed_packages_hotel_shop_loading";
export const VIEWED_PACKAGE_HOTEL_SHOP_MAP = "viewed_packages_hotel_shop_map";
export const VIEWED_PACKAGE_HOTEL_AVAILABILITY_MAP =
  "viewed_package_hotel_availability_map";
export const NAVIGATED_BACK_TO_PACKAGE_HOTEL_LIST =
  "navigated_back_to_package_hotel_list";
export const SELECT_PACKAGE_HOTEL_ROOM = "select_package_hotel_room";
export const VIEWED_ITINERARY_REVIEW = "viewed_itinerary_review";
export const REVIEWED_FLIGHT_DETAILS = "reviewed_flight_details";
export const PACKAGES_REVIEW_PAGE_CHANGE_HOTEL_SELECTED =
  "change_hotel_selected";
export const HOTELS_CLICKED_CHANGE_RESERVATION =
  "hotel_clicked_change_reservation";
export const CHANGE_ROOM_SELECTED = "change_room_selected";
export const CHANGE_FLIGHT_SELECTED = "change_flight_selected";
export const REDIRECT_TO_PACKAGE_HOTEL_SHOP = "redirect_to_package_hotel_shop";
export const PACKAGES_CHECKOUT_HANDOFF_FAILED =
  "packages_checkout_handoff_failed";
export const SELECTED_PACKAGE_FLIGHT_PAX = "selected_package_flight_pax";
export const SELECTED_PACKAGE_PRIMARY_TRAVELER =
  "selected_package_primary_traveler";
export const VIEWED_PACKAGE_CHECKOUT_PAYMENTS =
  "viewed_package_checkout_payments";
export const VIEWED_PACKAGE_CONFIRM_AND_BOOK =
  "viewed_package_confirm_and_book";
export const VIEWED_PACKAGE_CHECKOUT_BREAKDOWN =
  "viewed_package_checkout_breakdown";

export interface ViewedCheckoutBreakdownProperties {
  checkout_page: string;
}

export interface PackageEntryProperties {
  readonly market: string;
  readonly country: string;
  readonly los?: string;
  readonly number_of_adults: number;
  readonly number_of_children: number;
  readonly number_of_infants: number;
  readonly total_travelers: number;
  readonly non_stop: boolean;
  readonly fare_class?: FareclassShelfBrandName;
  readonly entry_type?: PackagesEntryTypeEnum;
}

export interface PackagesViewedHotelListProperties
  extends ViewedHotelListProperties {}

export interface PackagesHotelEntryProperties extends HotelEntryProperties {}

export interface BaseActivitiesProperties {
  credit_balance?: number;
  entry_type?: "cross_sell" | "generic_cta" | ActivitiesEntryTypeEnum;
  entry_types_all?: ActivitiesEntryTypeEnum[];
  environment?: string;
  rewards_accounts?: string;
  lodging_id?: string;
  stays_advance?: number;
  feed_placement?: string;
  origin_product_type?: string;
  xsell_click_target?: string;
}

export interface ActivitiesAvailabilityProperties
  extends BaseActivitiesProperties {
  advance?: number;
  location_searched?: string;
  filters_applied?: string[];
  tag_selected?: string;
}

export interface ActivitiesDetailsProperties
  extends ActivitiesAvailabilityProperties {
  activity_provider?: string;
  avg_rating?: number;
  activity_name?: string;
  ag_1?: number;
  ag_2?: number;
  ag_3?: number;
  ag_4?: number;
  ag_5?: number;
  age_groups?: string[];
  markup_percentage?: number;
  number_of_photos?: number;
  number_of_reviews?: number;
  original_net?: number;
  productId?: string;
  product_sort_index?: number;
  total_price?: number;
  rrp?: number;
}

export interface ActivitiesShopOptionProperties
  extends ActivitiesDetailsProperties {
  success?: boolean;
  activity_option_name?: string;
  option_code?: string;
}

export interface ActivitiesCheckoutProperties
  extends ActivitiesShopOptionProperties {
  error_type?: string;
  revenue?: number;
  total_card_amount_usd?: number;
  total_offers_amount_usd?: number;
  credit_amt_redeemed?: number;
  total_rewards_amount_usd?: number;
}

export const CLICKED_ACTIVITIES_HEADER = "clicked_activities_header";

export const ACTIVITIES_ENTRY = "activities_entry";
export interface ViewedExperiencesListProperties {
  rewards_accounts?: string;
  location_searched?: string;
  entry_type?: string;
}

export enum ActivitiesEntryTypeMap {
  HOMEPAGE = 0,
  ACTIVITIES_XSELL = 1,
  STAYS_XSELL = 2,
}

export const ACTIVITIES_SEARCHED_REVIEWS = "activities_searched_reviews";
export const VIEWED_ACTIVITY_IN_LIST = "viewed_activity_in_list";
export const VIEWED_ACTIVITY_DETAILS = "viewed_activity_details";
export const VIEWED_ACTIVITIES_CHECKOUT = "viewed_activities_checkout";

export const ACTIVITY_EXIT_SURVEY_SHOWN = "activity_exit_survey_shown";
export const ACTIVITY_EXIT_SURVEY_OPENED = "activity_exit_survey_opened";
export const ACTIVITY_EXIT_SURVEY_DISMISSED = "activity_exit_survey_dismissed";
export const ACTIVITY_EXIT_ANSWERED_QUESTION =
  "activity_exit_survey_answered_question";

export interface ViewedExperienceDetailsProperties
  extends ViewedExperiencesListProperties {
  activity_name?: string;
  avg_rating?: number;
  number_of_reviews?: number;
  number_of_photos?: number;
}

export const ACTIVITIES_VIEWED_PHOTOS = "activities_viewed_photos";

export interface ActivitiesAppliedFilterProperties
  extends ActivitiesAvailabilityProperties {
  filter_type?: string;
}
export const ACTIVITIES_APPLIED_FILTER = "activities_applied_filter";

export const ACTIVITIES_TAG_SELECTED = "activities_tag_selected";
export const PRICE_QUOTE_ACTIVITY = "activities_pq";
export const COMPLETE_BUY_ACTIVITY = "complete_buy_activities";
export const ACTIVITIES_SHOP_OPTION = "activities_shop_option";

export const VIEWED_SIMILAR_ACTIVITIES = "viewed_similar_activities";
export const CLICKED_SIMILAR_ACTIVITIES = "clicked_similar_activities";
export const VIEWED_COMPARE_ITINERARIES = "viewed_compare_itineraries";

export interface ActivitiesSimilarClickedProperties
  extends ActivitiesDetailsProperties {
  product_id_clicked?: string;
  product_name_clicked?: string;
  price_clicked?: number;
  duration_clicked?: number;
  rating_clicked?: number;
  product_index_clicked?: number;
}

export const ACTIVITIES_AUTOCOMPLETE_FOCUSED =
  "activities_autocomplete_focused";
export const ACTIVITIES_AUTOCOMPLETE_TYPED = "activities_autocomplete_typed";
export const ACTIVITIES_AUTOCOMPLETE_SUGGESTION_CLICKED =
  "activities_autocomplete_suggestion_clicked";

export const ActivitiesAutocompleteSuggestionType = {
  Trip: "trip",
  Search: "search",
  RecentSearch: "recent_search",
  Nearby: "nearby",
} as const;

export type ActivitiesAutocompleteSuggestionType =
  (typeof ActivitiesAutocompleteSuggestionType)[keyof typeof ActivitiesAutocompleteSuggestionType];

export interface ActivitiesAutocompleteFocusedProperties {
  trip_suggestions_loaded: boolean;
}

export interface ActivitiesAutocompleteTypedProperties {
  trip_suggestions_loaded: boolean;
}

export interface ActivitiesAutocompleteSuggestionClickedProperties {
  index: number;
  suggestion_type: ActivitiesAutocompleteSuggestionType;
  trip_suggestions_loaded: boolean;
}

// Secondary search (availability page keyword search) events
export const ACTIVITIES_SECONDARY_SEARCH_FOCUSED =
  "activities_secondary_search_focused";
export const ACTIVITIES_SECONDARY_SEARCH_TYPED =
  "activities_secondary_search_typed";
export const ACTIVITIES_SECONDARY_SEARCH_RESULT_CLICKED =
  "activities_secondary_search_result_clicked";

export const ActivitiesSecondarySearchResultType = {
  Category: "category",
  Attraction: "attraction",
  Activity: "activity",
} as const;

export type ActivitiesSecondarySearchResultType =
  (typeof ActivitiesSecondarySearchResultType)[keyof typeof ActivitiesSecondarySearchResultType];

export interface ActivitiesSecondarySearchResultClickedProperties {
  result_type: ActivitiesSecondarySearchResultType;
  result_index: number;
}

// Dateless search events
export const ACTIVITIES_DATELESS_SELECT_DATES_CLICKED =
  "activities_dateless_select_dates_clicked";
export const ACTIVITIES_UNAVAILABLE_DATE_MODAL_OPENED =
  "activities_unavailable_date_modal_opened";
export const ACTIVITIES_UNAVAILABLE_DATE_MODAL_DISMISSED =
  "activities_unavailable_date_modal_dismissed";
export const ACTIVITIES_UNAVAILABLE_DATE_NEXT_DATE_SELECTED =
  "activities_unavailable_date_next_date_selected";
export const ACTIVITIES_UNAVAILABLE_DATE_VIEW_ALTERNATIVES =
  "activities_unavailable_date_view_alternatives";

export enum CorporateReferralBannerSource {
  Homepage = "homepage",
  Confirmation = "confirmation",
}

export const ENTERED_USEFULNESS_SCALE = "entered_usefulness_scale";
export interface EnteredUsefulnessScaleProperties {
  usefulness?: number;
}

export const SUBMIT_FEEDBACK = "submit_feedback";

export interface SubmitFeedbackProperties
  extends EnteredUsefulnessScaleProperties {}

export const FAVORITES_ENTRY = "favorites_entry";
export const FAVORITES_VIEWED_MODAL = "favorites_viewed_modal";
export const FAVORITES_ADD_ITEM = "favorites_add_item";
export const FAVORITES_REMOVE_ITEM = "favorites_remove_item";
export const FAVORITES_CREATE_LIST = "favorites_create_list";
export const FAVORITES_VIEWED_LIST = "favorites_viewed_list";
export const FAVORITES_EDITED_LIST = "favorites_edited_list";
export const CUSTOMER_PROFILE_VIEWED_SECTION =
  "customer_profile_viewed_section";

export interface FavoritesEntryProperties {
  entry_type: FavoritesEntryType;
}

export type FavoritesEntryType =
  | "profile"
  | "stays_search"
  | "stays_availability"
  | "favorites_availability"
  | "hotel_shop"
  | "vr_shop"
  | "premium_stays_availability"
  | "premium_stays_vibes"
  | "premium_hotel_shop"
  | "premium_vr_shop";

export interface FavoritesListProperties {
  favorites_name?: string;
  favorites_id?: string;
  action?: "delete" | "edit_dates" | "edit_guests";
}

export type FavoritesItemType =
  | "hotel"
  | "vr"
  | "lifestyle_hotel"
  | "premier_hotel"
  | "lifestyle_vr"
  | "premier_vr";

export interface FavoritesItemProperties {
  item_id?: string; // lodging_id or listing_id
  item_type?: FavoritesItemType;
}

export type LodgingDetailsEntrySource = "favorites_list" | "favorites_map";

export const APP_GTM_ENTRY = "app_gtm_entry";

export const APP_GTM_EXIT = "app_gtm_exit";

// TRAVEL APP

export type TravelAppInterfaceEventPayload = {
  eventName: string;
  funnelType: string | undefined;
  eventData: unknown;
};

export const TRAVEL_APP_INTERFACE_EVENT = "travel_app_interface_event";

// PAYMENT FLEX SMOKE TEST (cot_payment_flex_survey)

export const PAY_FLEX_SURVEY_LINK_VIEWED = "pay_flex_survey_link_viewed";
export const PAY_FLEX_SURVEY_CHOICES_VIEWED = "pay_flex_survey_choices_viewed";
export const PAY_FLEX_SURVEY_OPTION_TAPPED = "pay_flex_survey_option_tapped";
export const PAY_FLEX_SURVEY_SUBMITTED = "pay_flex_survey_submitted";
export const PAY_FLEX_SURVEY_DISMISSED = "pay_flex_survey_dismissed";

export interface PayFlexSurveyBaseProperties {
  card_product_used: string;
  travel_product: "hotel" | "flight";
  total_amount_usd: number;
  card_amount_usd: number;
  rewards_amount_usd: number;
  credits_amount_usd: number;
}

export type PayFlexSurveyOptionSelected =
  | "6_month_plan"
  | "12_month_plan"
  | "not_interested";

export interface PayFlexSurveyOptionProperties
  extends PayFlexSurveyBaseProperties {
  pay_flex_survey_option_selected: PayFlexSurveyOptionSelected;
}

export const INSURANCE_OFFER_SHOWN = "insurance_offer_shown";
export const INSURANCE_OPT_IN_CLICKED = "insurance_opt_in_clicked";
export const INSURANCE_SURVEY_RESPONSE_SUBMITTED =
  "insurance_survey_response_submitted";

export interface InsuranceBaseProperties {
  funnel: string;
  card_products: string[];
  cfar_attached?: boolean;
  fda_attached?: boolean;
}

export interface InsuranceSurveyResponseSubmittedProperties
  extends InsuranceBaseProperties {
  coverage_options_selected: string[];
  likelihood_to_purchase: number;
}

// TRIPS REPLATFORM NOTICE BANNER EVENTS
export const VIEWED_TRIPS_REPLATFORM_NOTICE = "viewed_trips_replatform_notice";
export const CLICKED_CLOSE_TRIPS_REPLATFORM_NOTICE =
  "clicked_close_trips_replatform_notice";
export const CLICKED_NAVIGATE_TO_PORTAL_TRIPS_REPLATFORM_NOTICE =
  "clicked_navigate_to_portal_trips_replatform_notice";
